import { TOOLTIP_CHROME, supersetTooltipFormatter } from '../charts/metric-tooltip';
import { GOAL_LINE_COLOR } from '../charts/palette';
import { DRILLDOWN_EVENT, returnFocusAfterDrilldown, returnFocusAfterModal, safeDeeplink } from '../charts/cell-actions';
import { NO_FILTER } from '../filters/date-filter-value';
import { DROP_CANCEL_EVENT, GRAB_EVENT } from './site-analytics-arrange';
import ReportTabDrag from './report-tab-drag';
import {
    PALETTE_ANNOUNCE_DELAY,
    focusRevealed,
    paletteAddAfter,
    paletteCountSentence,
    paletteShownText,
    pinPanelTop,
    searchWords,
    foldForSearch,
    showJustAdded,
} from './arrange-edit';

// The Add chart panel's rules moved to arrange-edit.js, which Site Analytics' edit mode shares;
// re-exported so everything that imports them from here still finds them.
export {
    PALETTE_ANNOUNCE_DELAY,
    PALETTE_JUST_ADDED_MS,
    foldForSearch,
    paletteAddAfter,
    paletteCountSentence,
    paletteMatches,
    paletteShownText,
    searchWords,
} from './arrange-edit';

/**
 * The Alpine half of a Blink Report's page (livewire/report/show.blade.php): it takes the one
 * `report-cards` event each tab load dispatches and hands every card its part, and it works the
 * tablist of a report of several tabs.
 *
 * The event's detail is `{reportId, tabId, tabName, cards: {<cardId>: detail}}`, every card on the
 * tab keyed by its id, in tab order, and the tab it answers for. A card's detail carries:
 *
 *  - for a card drawn through <x-charts.superset-chart>: either `contract` (with `tooltip`, the
 *    strings its hover tooltip prints and the name of each period it draws, for a metric card
 *    drawn on a canvas, and `key`, the entries of the static key under it, from ReportCardKey) or
 *    `status` ('empty' | 'failed') with `emptyMessage`. Forwarded to the host
 *    `report-<reportId>-<cardId>` as a `superset-chart-data` event, the tooltip built here, because
 *    the host takes one only from script, with the same period names as `periods`, so the host's
 *    sr-only table calls each period what the tooltip's header does, and with `styleColors`, which
 *    ink a line card's goal in the app's goal grey. A metric card's contract also comes with
 *    `actions`, the drilldown behind each drawn period (MetricCardActions), and `deeplink`, the
 *    source's own records over the span it draws (MetricCardDeeplinks); neither on the public link
 *    or for a reader the metric's drilldown refuses. The card's expand icon opens its periods' whole
 *    span, its ↗ icon links to the deeplink, and the host is sent `periodClicks` so a mouse click on
 *    the chart opens the period clicked (below);
 *  - for a scorecard grid card, drawn through <x-charts.superset-table>: either `table`
 *    (ScorecardGridResult::viewerTable()) with `lockedDrilldownRows`, the rows whose drilldown this
 *    viewer may not open, or `status` ('empty' | 'failed') with `emptyMessage`. Forwarded to the
 *    table host by the same id spelling on the same `superset-chart-data` event, since the table
 *    host listens for it as the chart host does and each answers only its own ids;
 *  - for every card: `notes` (a metric that is switched off, or disconnected, or private to the
 *    reader, a donut's "(other)" and "Data through"), `notApplied` (the sentence of each report
 *    filter that reached the card and could not narrow it), `missedFilters` (how many of those would
 *    have narrowed it, which its ⓘ marks with a dot) and `filtersApplied` (the value filters that
 *    did), all listed in the card's ⓘ panel (report/card-info.blade.php, below).
 *
 * Routing is by card id, never by content: a report may hold two cards on one metric, and each
 * draws its own figures. The host id carries the report's id as well as the card's, because card
 * ids are only unique within a report and the host router replays what it holds to any host that
 * mounts with a matching id.
 *
 * A detail the host already holds is never sent again: a new contract re-mounts the chart and
 * replays its entry animation. So every load first puts each card that shows no chart back to
 * 'loading' through that same comparison, and a failure that repeats after Retry is sent, not
 * swallowed as unchanged. Apply filters and Reset go further: as either is pressed, every card goes
 * back to loading, a drawn chart included, so nothing drawn from the old values stays up under the
 * new ones (rp_filtersApplying()). Until either is pressed, the bar says "Changes not applied" from
 * the moment a control differs from what the cards were drawn with (rp_filtersPending()), and the
 * status region says so once (rp_filtersChanged()).
 *
 * Tabs. The page shows one tab, the one the server last drew (`$wire.drawnTabId`); the tablist
 * follows the APG tabs pattern with manual activation, because each switch is a Livewire round
 * trip: arrow keys and Home/End move focus only (rp_tabKeydown()), and a press, Enter or Space
 * included, sets `$wire.tab` (rp_openTab()), which the server validates, draws and follows with the
 * new tab's load. Back and forward set the same property. When the tab drawn changes (rp_sync(),
 * run from every commit before Livewire morphs the page, and before any answer is routed), nothing
 * of the old tab survives: every host id the page sent anything to is forgotten by the hosts'
 * routers (`superset-chart-forget`), so a card's host that mounts again on a later visit starts
 * from its skeleton instead of replaying what an earlier visit was sent, and the comparison and the
 * footers start empty. An answer for a tab the page no longer shows is dropped. The status region
 * names the tab once its cards are in.
 *
 * Arrange mode. Each arrange action on the server answers in one `report-arrange` event (Show::
 * arrangeEvent()): a sentence for the arrange status region, which is its own region so a load
 * that follows never talks over it, where focus goes once the morph has landed (the pressed
 * control is often gone: a card removed, the bar's buttons swapped), and the cards to load again.
 * Those loads, and the one a card's data source change asks for, are PARTIAL: loadTab() is handed
 * the card ids and answers only them (`partial: true`), so no other chart on the tab is read or
 * redrawn, and the status region speaks only if a card failed. A load asked for while one is out
 * is queued, not dropped, and runs when the one out settles. An arrange call that fails (edit
 * rights taken away mid-session) says so and reloads the page's arrangement from the server.
 *
 * Tab management, in edit mode, answers through the same event. Its controls sit in the tab row
 * (components/report/tab-strip.blade.php), outside the tablist: the open tab's options, the name
 * field Rename opens over the tab, and Add tab. They read the open tab's id with rp_tabOf(). A
 * focus instruction may name `tab` (the open tab's button in the tablist, after a delete, a
 * duplicate or a rename, else the options button when one tab is left and so no tablist) or
 * `tab-control` (one of those controls; a move button disabled at an end hands focus to the other
 * one, then to the name field). `tab` closes the name field and a `name` target opens it
 * (`rp_tabRenaming`); `select` also selects its text, so typing names a new tab. Undo answers with
 * `undo`, its own button, which stays focusable when there is nothing left to undo.
 *
 * The tab row does not scroll. rp_layoutTabs() measures it after every render and on every resize,
 * hides the tabs past its end (never the open one) and lists them under "More (N)", and places the
 * open tab's options and name field against it, since neither can be the tab's DOM sibling inside
 * the tablist.
 *
 * The Add chart panel's search and source filter (components/report/palette.blade.php) live here
 * too, as `rp_palQuery` and `rp_palSources`, and run in the browser: the panel lists every metric
 * the editor may pin when nothing is typed, so the whole list is sent when it opens anyway, and
 * narrowing it on the server would cost a full render per keystroke. Entries and groups are hidden
 * with x-show over the server's markup, which a morph keeps, so a re-render while the panel is
 * open (an add, which leaves it open) keeps what the editor typed and what the list shows. The count
 * is said through the arrange region, a moment after the last change rather than on every
 * keystroke. The panel's sections (`rp_palSections`) and each metric's "Add as" menu
 * (`rp_palAs*`, one menu the whole list shares) are the panel's own disclosures.
 *
 * A card's ⓘ (report/card-info.blade.php) opens a panel of what the load says about the card, and
 * is drawn only when there is something to list. One panel is open on the page at a time
 * (`rp_infoCard`, the open card's slot), so the page, not each card, closes it: a click outside it,
 * Escape (which hands focus back to its button), a grab to drag, focus leaving it, a tab switch, and
 * a load that leaves the card nothing to list. Its panel exists only while open, placed by x-anchor.
 *
 * A metric card's toolbar (report/card.blade.php) holds expand, ↗ and, for the report's editors,
 * the pen.
 *
 * Expand opens the card's expanded view (rp_openExpanded()): DrilldownTable's modal, the one the
 * page renders, with a copy of the card's chart above the records. The copy is drawn in the modal's
 * host (`config.expandedHostId`), sent the card's own contract before the modal draws it: the host
 * router hands a host what was sent for its id when it mounts. The records start on the whole span
 * the card draws, from its first period to its last; a card with no periods to open (no
 * drilldown, or one this reader may not open) shows its chart alone. A mouse click on one of the
 * copy's periods narrows the records to that period, and the modal offers the way back.
 *
 * A mouse click on the card's own chart opens the expanded view already narrowed to the period
 * clicked, which the host reports as `superset-chart-period-click` {id, instant, pointerType}: the
 * period the tooltip shows, or a null instant for a click off the plot, which on a Big Number is its
 * figure, the headline. A touch or pen tap opens nothing, so it keeps showing the tooltip, and the
 * tooltip says a click opens something only under a period one does. Every date is looked up again
 * in what the card was sent, never taken from the page. When the modal closes, focus goes back to
 * what opened it: the expand icon, or the chart's figure, which a click on the chart focuses.
 *
 * ↗ is a plain link to the source's own records over the same span (`deeplink`), in a new tab, and
 * only once safeDeeplink() has passed it, as a grid cell's is.
 *
 * The pen opens the type editor at once (rp_editType()): the dialog shows the moment it is pressed,
 * with the card's own chart drawn in its preview host (`config.previewHostId`) from what the page
 * holds, and the server is only told which card it is changing (Show::openTypeEditor()). Another
 * type is previewed by the server (rp_previewType(), Show::previewCardType()) as
 * `report-card-preview` {reportId, hostId, type, card}: `card` is the detail a load would send the
 * card drawn as that type, and rp_preview() draws it only while that type is still the one chosen.
 * Saving redraws the card through a load of its own.
 */

const DATA_EVENT = 'superset-chart-data';

/** A mouse click on a chart opens its period; a touch or pen tap shows the tooltip. */
const CLICKING_POINTER = 'mouse';

/**
 * Tells the chart and table hosts' routers to drop what they hold for these ids
 * (superset-chart.js, superset-table.js), so a host mounting under one of them later starts from its
 * first paint.
 */
const FORGET_EVENT = 'superset-chart-forget';

/**
 * A card's type choices as its pen or Type carries them (data-type-choices, Show::typeChoicesFor()):
 * each {value, label} a string pair, anything else dropped.
 *
 * @param {?string} json
 * @returns {Array<{value: string, label: string}>}
 */
export function typeChoicesOf(json) {
    let parsed;

    try {
        parsed = JSON.parse(json ?? '[]');
    } catch {
        // Convention exception — a parse failure handled as no choices:
        // the attribute is the server's json_encode(), so this is only a hand-edited page; the
        // dialog then lists nothing to choose rather than failing to open.
        return [];
    }

    return Array.isArray(parsed)
        ? parsed.filter((choice) => typeof choice?.value === 'string' && typeof choice?.label === 'string')
        : [];
}

/** What the comparison holds for a host told to load: equal to no answer. */
const LOADING = Object.freeze({ compared: null, drawsChart: false });

/** Every host a card mounts: a chart's and a scorecard grid's table's, each carrying its id. */
const HOST_ATTRIBUTES = ['data-superset-chart', 'data-superset-table'];

/**
 * Report\Show's arrange actions: a failed call to any of them leaves the screen showing a change
 * the server refused, so the page says so and draws the server's arrangement again.
 */
export const ARRANGE_METHODS = new Set([
    'startArranging', 'saveLayout', 'discardLayout', 'togglePalette', 'closePalette', 'moveCardBy', 'moveCardDragged',
    'setSpan', 'setHeight', 'resizeCard', 'removeCard', 'addCard', 'addCardAt', 'removeRow',
    'addTab', 'renameTab', 'moveTabBy', 'moveTabDragged', 'askDeleteTab', 'deleteTab', 'moveCardToTab',
    'duplicateTab', 'undo', 'removeCardAndEdit',
    'moveGridRow',
    'addCardAs', 'openPaletteForGrid', 'openPaletteToReplace',
]);

/**
 * The filter bar's Apply filters and Reset, and the chips' ✕ and Clear all under it
 * (filters/report-filter-bar.js). Pressing any of them puts every card back to loading at once
 * (rp_filtersApplying()), so a call that fails leaves skeletons no load will answer: each one fails
 * instead, with its Retry.
 */
export const FILTER_METHODS = new Set(['applyFilters', 'resetFilters', 'removeFilterValue', 'clearFilters']);

/**
 * A card's edit controls a focus instruction may name; anything else falls back to the card's
 * heading. `add-row` is a scorecard grid's Add row, which the Add chart panel's Add row mode hands
 * focus back to when it closes; `replace-metric` is a removed-metric card's Replace metric, which
 * its Replace metric mode hands focus back to.
 */
const ARRANGE_CONTROLS = new Set(['move-up', 'move-down', 'size', 'move', 'type', 'rows', 'source', 'move-tab', 'remove', 'add-row', 'replace-metric']);

/** The tab row's edit controls a focus instruction may name; anything else falls back to the name field. */
const TAB_CONTROLS = new Set(['name', 'options', 'rename', 'rename-save', 'duplicate', 'move-earlier', 'move-later', 'delete', 'add']);

/** How rp_layoutTabs() takes a tab out of the row: Tailwind's `hidden`, which the server never draws on a tab. */
const TAB_HIDDEN = 'hidden';

/** Where focus goes when a tab move button it was sent to has just become disabled at an end. */
const TAB_CONTROL_FALLBACK = { 'move-earlier': 'move-later', 'move-later': 'move-earlier' };

/** Where focus goes when a card's move button it was sent to has just become disabled at an end. */
const CONTROL_FALLBACK = { 'move-up': 'move-down', 'move-down': 'move-up' };

/**
 * The next load to ask for, given the one already queued and a new request: null is the whole tab,
 * and wins over any list; two lists are joined. `undefined` means nothing is queued.
 *
 * @returns {?string[]|undefined}
 */
export function queuedLoad(queued, requested) {
    if (queued === null || requested === null) return null;

    const ids = [...(queued ?? []), ...requested].filter((id) => typeof id === 'string');

    return [...new Set(ids)];
}

/**
 * The id of a card's host, a chart's or a table's. Show::HOST_ID_PREFIX builds the same id in Blade, and
 * ReportPageModuleTest holds the two spellings together.
 */
export function hostIdFor(reportId, cardId) {
    return `report-${reportId}-${cardId}`;
}

/**
 * The part of a card's detail that reaches its host, or null for a card with no host.
 *
 * @returns {{contract: object}|{table: object, lockedDrilldownRows: string[]}|{status: string, emptyMessage: string}|null}
 */
export function hostPart(card) {
    if (card?.contract && typeof card.contract === 'object') {
        return { contract: card.contract };
    }

    // Sent even when empty, as the table host keeps the last locked rows it was given, and a row
    // this viewer may now open must get its buttons back.
    if (card?.table && typeof card.table === 'object') {
        return {
            table: card.table,
            lockedDrilldownRows: Array.isArray(card.lockedDrilldownRows) ? card.lockedDrilldownRows.filter((key) => typeof key === 'string') : [],
        };
    }

    if (card?.status === 'empty' || card?.status === 'failed') {
        return { status: card.status, emptyMessage: typeof card.emptyMessage === 'string' ? card.emptyMessage : '' };
    }

    return null;
}

/**
 * The scorecard's name for each period a card draws (instant => name, from MetricCardTooltip), or
 * null. PHP encodes an empty map as a list, which names nothing.
 *
 * @returns {?Object<number, string>}
 */
export function periodsOf(tooltip) {
    const periods = tooltip?.periods;

    return periods && typeof periods === 'object' && ! Array.isArray(periods) ? periods : null;
}

/**
 * The app's metric tooltip over the Superset series a card drew, as the host's `tooltip` option.
 *
 * A Big Number's trendline is one unnamed series, so each of its points is handed to the shared
 * formatter under the card's own label, which is the one its strings are keyed by. Its header names
 * the hovered period as the scorecard's column does ("September 2026", never "Sep 1, 2026"), from
 * the names MetricCardTooltip sends; the contract's time grain names any period it left out, and a
 * Big Number, which sends no grain, falls back to the day.
 *
 * @param {object}  contract  the drawn contract
 * @param {?object} tooltip   {labels, displays, goal, periods} from MetricCardTooltip, or null
 * @param {object}  options   {locale, text: {actual, projected, goal}, hint}: `hint` is (instant) =>
 *                            the line under the hovered period saying what a click there does, or null
 */
export function tooltipFor(contract, tooltip, { locale, text = {}, hint = null } = {}) {
    if (! tooltip || typeof tooltip !== 'object' || ! tooltip.labels) return null;

    const formatter = supersetTooltipFormatter(
        { labels: tooltip.labels, displays: tooltip.displays ?? {}, goal: tooltip.goal ?? null },
        { locale, text, grain: contract?.formData?.timeGrainSqla ?? null, periods: periodsOf(tooltip), hint },
    );

    const format = contract?.vizType === 'big_number'
        ? (params) => formatter((Array.isArray(params) ? params : [params])
            .filter(Boolean)
            .map((item) => ({ ...item, seriesName: tooltip.labels.actual })))
        : formatter;

    return { ...TOOLTIP_CHROME, trigger: 'axis', formatter: format };
}

/**
 * The colours a card's contract is drawn in beyond the palette's: a line card's goal in the app's
 * goal grey, as the scorecard drawer draws its goal (its dash and its place behind the metric's
 * line travel in the contract's series styles). The goal's label is the tooltip's, which names only
 * a series the contract draws; a card with no goal line sends null, so a card that loses its goal
 * drops the grey with it.
 *
 * @returns {?{series: Object<string, string>}}
 */
export function styleColorsFor(tooltip) {
    const goal = tooltip?.labels?.goal;

    return typeof goal === 'string' && goal !== '' ? { series: { [goal]: GOAL_LINE_COLOR } } : null;
}

/** A drilldown's keys, the `show-drilldown-modal` event's, or null for anything else. */
function drilldownOf(detail) {
    if (! detail || typeof detail !== 'object') return null;

    const { metricId, metricName, startDate, endDate } = detail;

    return Number.isInteger(metricId) && typeof startDate === 'string' && typeof endDate === 'string'
        ? { metricId, metricName: typeof metricName === 'string' ? metricName : '', startDate, endDate }
        : null;
}

/**
 * The drilldown behind each of a metric card's periods, from its load's `actions` (MetricCardActions,
 * as MetricCardResult::viewerActions() sends them): newest first, each with the instant the chart
 * draws it at, its name and its drilldown (the `show-drilldown-modal` event's keys). An entry without
 * all three is dropped, so nothing the page offers opens a malformed drilldown.
 *
 * @returns {Array<{instant: number, name: string, drilldown: object}>}
 */
export function actionsOf(list) {
    if (! Array.isArray(list)) return [];

    return list.flatMap((entry) => {
        const instant = Number(entry?.instant);
        const drilldown = drilldownOf(entry?.drilldown);

        return Number.isFinite(instant) && typeof entry.name === 'string' && drilldown
            ? [{ instant, name: entry.name, drilldown }]
            : [];
    });
}

/** Whether a click on a card's chart opens anything. */
export function opensOnClick(actions) {
    return actions.length > 0;
}

/**
 * The drilldown the card's expand icon opens: the whole span of its periods, from the earliest one's
 * start to the latest one's end (which the period still running already ends today), under the name
 * the card's drilldowns carry. Null for a card that opens nothing. Dates are compared as the
 * 'Y-m-d' strings they are.
 *
 * @returns {?{metricId: number, metricName: string, startDate: string, endDate: string}}
 */
export function spanOf(actions) {
    if (! Array.isArray(actions) || actions.length === 0) return null;

    const drilldowns = actions.map((entry) => entry.drilldown);
    const startDate = drilldowns.map((drilldown) => drilldown.startDate).reduce((earliest, date) => (date < earliest ? date : earliest));
    const endDate = drilldowns.map((drilldown) => drilldown.endDate).reduce((latest, date) => (date > latest ? date : latest));

    return { ...drilldowns[0], startDate, endDate };
}

/**
 * The id a scorecard grid row's expanded chart is kept under beside the cards' (rp_gridRowOpen()),
 * so the expanded view's period clicks find its periods as they find a card's. Never a card id:
 * a stored card id carries no JSON.
 */
export function gridRowChartId(cardId, rowKey) {
    return `grid-row:${JSON.stringify([String(cardId), String(rowKey)])}`;
}

/**
 * The `show-drilldown-modal` detail Show::gridRowChart() answers with, held to the keys and types
 * DrilldownTable::openDrilldown() takes, or null for anything else.
 */
export function gridRowDrilldown(detail) {
    if (! detail || typeof detail !== 'object' || ! Number.isInteger(detail.metricId)) return null;

    const text = (value) => (typeof value === 'string' ? value : '');

    return {
        metricId: detail.metricId,
        metricName: text(detail.metricName),
        startDate: text(detail.startDate),
        endDate: text(detail.endDate),
        spanStartDate: text(detail.spanStartDate),
        spanEndDate: text(detail.spanEndDate),
        periodName: text(detail.periodName),
        hasRecords: detail.hasRecords === true,
    };
}

/**
 * What a click on a card's chart off its plot opens: on a Big Number, its figure, which shows the
 * headline, the newest period with a reading and so the first action; on any other card nothing.
 */
function figureAction(card) {
    return card?.vizType === 'big_number' ? (card.actions?.[0] ?? null) : null;
}

const SWATCHES = new Set(['line', 'dashed', 'slice']);

/**
 * The static key under a chart, from the entries its load detail carries (ReportCardKey): printed in
 * the same frame as the card's footer, before the chart draws, so the row already has its final
 * height when each of its charts draws and no drawn chart is moved by a key landing later.
 *
 * A chart of several series gets one, since Superset's legend is mouse-only and the chart draws
 * none. A donut always gets one, even of one slice, with each slice's figure: its slices carry no
 * labels (ReportCardType::formDataOverrides()), so without the key a slice would be told apart by
 * its colour alone and its figure read only by hovering.
 *
 * Each swatch is a box of fixed size whose colour arrives with the draw (keyInDrawnColours), so it
 * starts grey. A card whose key is unchanged keeps the colours it was drawn in: its host is not sent
 * the same contract again, so no draw would bring them back.
 *
 * @returns {Array<{name: string, value: ?string, swatch: string, color: ?string}>}
 */
export function keyOf(entries, previous = []) {
    if (! Array.isArray(entries)) return [];

    return entries
        .filter((entry) => entry && typeof entry.name === 'string' && SWATCHES.has(entry.swatch))
        .map((entry) => {
            const kept = previous.find((earlier) => earlier.name === entry.name && earlier.swatch === entry.swatch);

            return {
                name: entry.name,
                value: typeof entry.value === 'string' && entry.value !== '' ? entry.value : null,
                swatch: entry.swatch,
                color: kept?.color ?? null,
            };
        });
}

/**
 * The key in the colours its host reports drawing: each entry's by its name, since Superset orders
 * the series itself, else by its place when the host named nothing. A colour only when the chart
 * drew a plain string; a gradient has none. Nothing else changes, so neither does the card's size.
 */
export function keyInDrawnColours(key, detail) {
    const names = Array.isArray(detail?.names) ? detail.names.map(String) : [];
    const colors = Array.isArray(detail?.colors) ? detail.colors : [];

    return key.map((entry, index) => {
        const at = names.length ? names.indexOf(entry.name) : index;
        const color = at >= 0 ? colors[at] : undefined;

        return { ...entry, color: typeof color === 'string' ? color : entry.color };
    });
}

/** Whether a card's entry in rp_cards has anything for its ⓘ to list: a sentence or an applied filter. */
function hasInfo(card) {
    return (card?.lines?.length ?? 0) > 0 || (card?.applied?.length ?? 0) > 0;
}

/** How many of one filter's values the "Filters applied:" line of a card's ⓘ shows before "+N more". */
export const INFO_VALUES_SHOWN = 3;

/**
 * A card's applied value filters as its load detail carries them (Show::footer()'s
 * `filtersApplied`): each {filter, name, values}, its values strings, anything else dropped, and a
 * filter left with no value dropped too.
 *
 * @returns {Array<{filter: string, name: string, values: string[]}>}
 */
export function appliedFiltersOf(list) {
    if (! Array.isArray(list)) return [];

    return list
        .filter((entry) => entry && typeof entry.filter === 'string' && typeof entry.name === 'string' && Array.isArray(entry.values))
        .map((entry) => ({ filter: entry.filter, name: entry.name, values: entry.values.filter((value) => typeof value === 'string' && value !== '') }))
        .filter((entry) => entry.values.length > 0);
}

/**
 * The "Filters applied:" line of a card's ⓘ in words, or null with none applied. Each value reads
 * as the bar's options label it (`options`, `$wire.filterOptions`: filter id => [{value, label}],
 * read once the tab's figures are in), else as sent: a country's name once the options land, its
 * code until then. `short` is what the panel shows, each filter's values cut to INFO_VALUES_SHOWN
 * and "+N more" (`more`, with `:count`); `full` names every value, for a screen reader.
 *
 * @returns {?{short: string, full: string}}
 */
export function appliedFiltersText(applied, options, more = '+:count more') {
    if (! Array.isArray(applied) || applied.length === 0) return null;

    const lines = applied.map((entry) => {
        // A filter id is stored free text, so it is looked up as an own key only.
        const listed = options && typeof options === 'object' && Object.prototype.hasOwnProperty.call(options, entry.filter) && Array.isArray(options[entry.filter]) ? options[entry.filter] : [];
        const labels = entry.values.map((value) => {
            const label = listed.find((option) => option?.value === value)?.label;

            return typeof label === 'string' && label !== '' ? label : value;
        });
        const shown = labels.slice(0, INFO_VALUES_SHOWN);
        const rest = labels.length - shown.length;

        return {
            // Joined, not a translated pattern: a filter's name is its author's text.
            short: `${entry.name}: ${shown.join(', ')}${rest > 0 ? ` ${more.replace(':count', String(rest))}` : ''}`,
            full: `${entry.name}: ${labels.join(', ')}`,
        };
    });

    return { short: lines.map((line) => line.short).join(' · '), full: lines.map((line) => line.full).join('; ') };
}

/**
 * Where focus goes from the tab at `index` of `count` on `key`, or null for a key the tablist does
 * not handle: Left and Right step and wrap, Home and End jump to the ends. Enter and Space are the
 * tab button's own click, which opens it (manual activation).
 *
 * @returns {?number}
 */
export function tabIndexAfterKey(key, index, count) {
    if (! Number.isInteger(index) || index < 0 || index >= count) return null;

    switch (key) {
        case 'ArrowRight': return (index + 1) % count;
        case 'ArrowLeft': return (index - 1 + count) % count;
        case 'Home': return 0;
        case 'End': return count - 1;
        default: return null;
    }
}

/**
 * Which tabs the row hides, by index, given each tab's width (margins included), the open tab's
 * index, the room the tabs have when every one fits, the gap between two tabs and the room "More"
 * takes once it is needed. Every tab fits: none. Otherwise the open tab is always kept, and the
 * others are kept in order while they fit beside it and More; from the first that does not, the
 * rest go to More, so the row never skips a tab and shows a later one.
 *
 * @returns {number[]}
 */
export function tabsToHide(widths, openIndex, available, gap, moreWidth) {
    const count = Array.isArray(widths) ? widths.length : 0;

    if (count === 0) return [];

    const total = widths.reduce((sum, width) => sum + width, 0) + gap * (count - 1);

    if (total <= available) return [];

    const budget = available - moreWidth;
    let used = openIndex >= 0 && openIndex < count ? widths[openIndex] : 0;
    let isFull = false;
    const hide = [];

    widths.forEach((width, index) => {
        if (index === openIndex) return;

        const needed = used === 0 ? width : used + gap + width;

        if (! isFull && needed <= budget) {
            used = needed;
        } else {
            isFull = true;
            hide.push(index);
        }
    });

    return hide;
}

/** A sentence with `:tab` in it, filled with a tab's name, which may hold any character a replace pattern would read. */
export function withTabName(template, name) {
    return String(template).split(':tab').join(name);
}

/**
 * The filter values to hand Report\Show::restoreFilters() when Back or Forward lands on a history
 * entry, or null when there is nothing to restore. Livewire restores the pushed `tab` from history
 * itself but never the replaced `filters`, whose last value it keeps in each entry's state
 * (`state.alpine.filters.value`, copied into every entry a tab push creates). Without this the
 * address would show one entry's values while the page kept drawing another's.
 *
 * An entry with no filter state (one a different page wrote) restores nothing; an entry whose values
 * match the page's is left alone; an emptied entry restores "no values". PHP sends an empty array as
 * a list, so an empty list and an empty map are the same values.
 */
export function filtersToRestore(state, current) {
    const entry = state?.alpine?.filters;

    if (! entry || typeof entry !== 'object' || ! ('value' in entry)) return null;

    const canonical = (values) => JSON.stringify(values && typeof values === 'object' && Object.keys(values).length ? values : {});
    const value = entry.value ?? {};

    return canonical(value) === canonical(current) ? null : value;
}

/**
 * A filter value as the bar compares it, so that what a control shows the same compares the same:
 * no value, the empty string (the address's cleared value), an empty list and a date range's
 * "No filter" (what its host writes when its picker is cleared, the same empty picker; Show::
 * pickedValues() applies it as no value) are one value, and a list is compared as a set, since a
 * host may hand the same ticks back in another order.
 *
 * @returns {string}
 */
export function filterValueKey(value) {
    if (value === null || value === undefined || value === '' || value === NO_FILTER) return 'null';

    if (typeof value === 'object') {
        const items = Object.values(value).map((item) => String(item)).sort();

        return items.length ? JSON.stringify(items) : 'null';
    }

    return JSON.stringify(String(value));
}

/**
 * Whether the filter bar's controls hold anything the cards were not drawn with. `pending` is what
 * the controls hold now (Show::$pending, which a control changes in the browser before any round
 * trip) and `applied` what they hold under the applied values (Show::$appliedBar). Only the
 * report's filters are compared, the ones `applied` names, each through filterValueKey(), so a pick
 * put back or the same ticks in another order is not a change. Read by own key: a filter id may be
 * any addressable name, "constructor" included.
 */
export function filtersPending(pending, applied) {
    if (! applied || typeof applied !== 'object') return false;

    const own = (values, id) => (values && typeof values === 'object' && Object.prototype.hasOwnProperty.call(values, id) ? values[id] : null);

    return Object.keys(applied).some((id) => filterValueKey(own(pending, id)) !== filterValueKey(applied[id]));
}

/** Every group of the open palette, each holding its entries. */
const PALETTE_GROUPS = '[data-report-palette] [data-palette-group]';

/** Every dataset chart entry of the open panel. */
const PALETTE_DATASETS = '[data-report-palette] [data-palette-dataset]';

/** The event that opens the page's data source dialog (Settings\DataSource::open()), here for a chart being added. */
export const PALETTE_DATASET_PICKER = 'show-report-data-source';

/**
 * The chart types a chart from connected data can be added as, from its row's data-palette-types
 * (`[{value, label}]`, the server's DatasetChartCatalog list): anything else reads as none.
 */
export function paletteChartTypes(raw) {
    try {
        const types = JSON.parse(String(raw ?? ''));

        return Array.isArray(types) ? types.filter((type) => typeof type?.value === 'string' && typeof type?.label === 'string') : [];
    } catch {
        return [];
    }
}

/**
 * Where the "Add as" menu goes, in px from the top of the panel's scrolling body: under its chevron
 * when it fits in the part of the body the editor can see, else above it, else as low as it can sit
 * while still fitting. `trigger` and `body` are viewport rects; `height` is the menu's.
 */
export function paletteMenuTop(trigger, body, scrollTop, height, viewportHeight = Infinity) {
    const gap = 4;
    const visibleTop = Math.max(body.top, 0);
    const visibleBottom = Math.min(body.bottom, viewportHeight);
    const below = trigger.bottom + gap;
    const above = trigger.top - gap - height;
    let top = below;

    if (below + height > visibleBottom) {
        top = above >= visibleTop ? above : Math.max(visibleTop, visibleBottom - height);
    }

    return Math.max(0, Math.round(top - body.top + scrollTop));
}

/**
 * Whether a "Row in <grid>" choice's grid already holds the metric a palette key names. `held` is
 * the choice's data-palette-held, the grid's metric ids separated by spaces; a key that names no
 * metric is held by nothing.
 */
export function paletteGridHolds(held, key) {
    const match = /^metric:(\d+)$/.exec(String(key ?? ''));

    return match !== null && String(held ?? '').split(/\s+/).includes(match[1]);
}

/** The pen's dialog (livewire/report/show.blade.php), watched so focus goes back to the pen when it closes. */
const TYPE_EDITOR_ID = 'report-type-editor';

export default function ReportPage(config = {}) {
    const reportId = String(config.reportId ?? '');
    const text = config.text ?? {};
    const prefix = hostIdFor(reportId, '');
    // Neither can be a card's host id, `report-<reportId>-<card id>` (Show::pageConfig()).
    const expandedHostId = typeof config.expandedHostId === 'string' ? config.expandedHostId : `report-expanded-${reportId}`;
    const previewHostId = typeof config.previewHostId === 'string' ? config.previewHostId : `report-preview-${reportId}`;

    // Host id => {compared, drawsChart} for the last detail each host was sent. Kept in the closure
    // rather than as reactive state (nothing renders from it), and a Map, so a stored card id can
    // never collide with an object's own keys.
    const sent = new Map();
    // Every host id sent anything since the page last changed tab, a failure included, which is
    // what the hosts' routers may hold and must forget when the tab goes.
    const touched = new Set();
    let loading = false;
    // The load out: null for the whole tab, else its card ids. And the load asked for while it was
    // out: undefined for none, null for the whole tab, else card ids.
    let out = null;
    let queued;
    let unhook = null;
    let unlistenHistory = null;
    let unlistenInfo = null;
    // The ⓘ (report/card-info.blade.php, its [data-card-info] root) whose panel is open: a click
    // inside it is not a click outside, and Escape hands focus back to its button. Kept in the
    // closure: nothing renders from it.
    let infoRoot = null;
    let stopWatchingDrilldown = null;
    let stopWatchingTypeEditor = null;
    // The tab row rp_layoutTabs() watches for size changes, and its observer.
    let observedStrip = null;
    let stripObserver = null;
    // A tab opened from "More": its button in the row takes focus once the row is laid out again,
    // since the pressed item leaves the list.
    let focusOpenTabAfterLayout = false;
    // A grid row's Move up or Move down pressed in its Rows menu: {cardId, rowKey, step}, focused
    // again once the move's answer has redrawn the menu (rp_arrange()).
    let pendingRowStep = null;
    // The page's own root element, kept from init(). A method called from a nested scope (a tab's
    // options, a card's menus) sees that scope's element as `$root`, so the tab row and card
    // handlers look the page up through pageRootOf() instead.
    let pageRoot = null;
    const pageRootOf = (scope) => pageRoot ?? scope?.$root;
    // Card id => {contract, tooltip}, the chart each card last drew from, which its expanded view
    // draws a copy of. Kept in the closure: nothing renders from it.
    const charts = new Map();
    // The card whose expanded view is open, which a click on the copy's periods narrows.
    let expandedCardId = null;
    // The card the type editor is changing, and the type it has: {id, type}, or null.
    let typeEditorCard = null;
    // The pointer that moved or pressed last, read when the tooltip asks whether to print its click
    // hint: a touch tap shows the tooltip too, and must not be told a click opens anything.
    let lastPointer = null;
    const trackPointer = (event) => {
        if (typeof event?.pointerType === 'string' && event.pointerType !== '') lastPointer = event.pointerType;
    };
    // The tab the page shows, as the server last drew it; '' before the scope has read it.
    let openTab = '';
    // Whether the filter bar was pending when rp_filtersChanged() last looked, so it speaks only as
    // the bar turns pending.
    let filtersWerePending = false;

    // The palette search's folded words, worked out once per change rather than once per entry,
    // and each entry's folded text, which only changes when a metric is renamed.
    let palQueryFor = '';
    let palWords = [];
    const palFolded = new Map();
    let palAnnounceTimer = null;
    // Whether the palette drag ending now was let go over the pinned edit bar or filter card, and so
    // put back into the panel (site-analytics-arrange.js, DROP_CANCEL_EVENT): its `end` keeps the
    // panel open. Set before that `end`, cleared by it and by the next grab.
    let palDropCancelled = false;
    const onDropCancel = (event) => {
        if (event?.detail?.from?.closest?.('[data-report-palette]')) palDropCancelled = true;
    };

    const cardIdOf = (hostId) => (typeof hostId === 'string' && hostId.startsWith(prefix) ? hostId.slice(prefix.length) : null);
    const send = (detail) => {
        touched.add(detail.id);
        window.dispatchEvent(new CustomEvent(DATA_EVENT, { detail }));
    };

    return {
        // Edit mode's drags onto the tab row: a card onto another tab, a tab to a new place.
        ...ReportTabDrag(),

        rp_status: '',

        /** What the arrange status region says. */
        rp_arrangeStatus: '',

        /** The palette's search text and chosen source group key ('' for every source). */
        rp_palQuery: '',
        /** The sources the Sources control narrows to, by group key (`source:<id>`, `other`); none is every source. */
        rp_palSources: [],

        /** Whether the Sources control's list is open. */
        rp_palSourcesOpen: false,

        /** The type editor's heading (the card's name) and the type chosen in it, which its radios bind. */
        rp_typeEditorTitle: '',
        rp_typeEditorChoice: '',
        /** The opened card's types, [{value, label}], read off its pen or Type (data-type-choices). */
        rp_typeEditorChoices: [],

        /** Whether the open tab's name field is showing over it (Rename, or a new tab). */
        rp_tabRenaming: false,

        /**
         * An entry of the palette is held (pressed, not yet dragged away): the palette shows only it.
         * rp_palGone: that entry has been dragged out, so the palette is out of the way until the
         * server closes it. See rp_palGrabbed().
         */
        rp_palHolding: false,
        rp_palGone: false,

        /** Which of the panel's sections are open: Metrics by default, the other two collapsed. */
        rp_palSections: { metrics: true, datasets: false, layout: false },

        /**
         * The metric whose "Add as" menu is open, by palette key ('' while it is closed), its name
         * (the menu's label) and where the one shared menu sits in the panel's scrolling body, in px
         * from its top, just under that metric's chevron. See rp_palAsToggle().
         */
        rp_palAsKey: '',
        rp_palAsName: '',

        /** A chart from connected data's own types, `{value, label}`, which the menu lists in place of the metric types; none for a metric. */
        rp_palAsTypes: [],

        /** That chart's default type, which the menu marks "default". */
        rp_palAsDefault: '',
        rp_palAsTop: 0,

        /**
         * Card id => {lines, missed, applied, key, drawn, chart, actions, deeplink, vizType}: what
         * each card's ⓘ lists (its notes and filter sentences, how many filters missed it, the value
         * filters that narrowed it) and its footer's key, whether its host has drawn the contract it
         * was last sent (see rp_keyHiddenFor()),
         * whether it was sent a chart at all (its expand icon), what each of its periods opens
         * (actionsOf()) on the chart it was sent, and the source link its ↗ icon opens
         * (safeDeeplink()). Keys are prefixed; see rp_slot().
         */
        rp_cards: {},

        /** The slot (rp_slot()) of the card whose ⓘ panel is open, '' for none. */
        rp_infoCard: '',

        init() {
            pageRoot = this.$root ?? null;
            this.rp_tabDragInit(pageRoot);
            openTab = this.rp_drawnTab();

            unhook = this.$wire.$hook('commit', ({ commit, succeed, fail }) => {
                // Livewire runs a response's succeed callbacks after merging its snapshot and before
                // it morphs the page, so a tab the response drew is known here while the old tab's
                // hosts are still on the page and the new tab's have not started.
                succeed(() => this.rp_sync());
                // Every response may change the tab row (a switch, a rename, a tab added), and a
                // morph puts back the server's markup; the frame after it lays the row out again.
                succeed(() => requestAnimationFrame(() => this.rp_layoutTabs()));

                if (commit.calls.some((call) => ARRANGE_METHODS.has(call.method))) {
                    fail(() => {
                        this.rp_arrangeAnnounce(text.arrangeFailed ?? 'That change could not be made. The arrangement was reloaded.');
                        this.$wire.$refresh();
                    });
                }

                if (commit.calls.some((call) => FILTER_METHODS.has(call.method))) {
                    fail(() => this.rp_failWaiting());
                }

                // A failed Livewire call never settles the promise it returned, so a load's outcome
                // is read off its request instead, the first load's included, which the server schedules.
                const load = commit.calls.find((call) => call.method === 'loadTab');

                if (! load) return;

                loading = true;
                out = Array.isArray(load.params?.[0]) ? load.params[0] : null;
                succeed(() => {
                    loading = false;
                    this.rp_flushQueued();
                });
                fail(() => {
                    loading = false;
                    this.rp_failWaiting();
                    this.rp_flushQueued();
                });
            });

            window.addEventListener('pointerdown', trackPointer, true);
            window.addEventListener('pointermove', trackPointer, true);
            window.addEventListener(DROP_CANCEL_EVENT, onDropCancel);

            // The open ⓘ panel's closers, one set for the page (see rp_infoToggle()). The click is
            // caught on its way down, so a handler stopping it further in cannot keep a panel open.
            const onInfoClick = (event) => {
                if (this.rp_infoCard !== '' && ! infoRoot?.contains?.(event.target)) this.rp_infoClose(false);
            };
            const onInfoKeydown = (event) => {
                if (event.key === 'Escape' && this.rp_infoCard !== '') this.rp_infoClose(true);
            };
            const onInfoGrab = () => this.rp_infoClose(false);
            window.addEventListener('click', onInfoClick, true);
            window.addEventListener('keydown', onInfoKeydown);
            window.addEventListener(GRAB_EVENT, onInfoGrab);
            unlistenInfo = () => {
                window.removeEventListener('click', onInfoClick, true);
                window.removeEventListener('keydown', onInfoKeydown);
                window.removeEventListener(GRAB_EVENT, onInfoGrab);
            };
            this.rp_revealOpenTab();
            // A web font that lands after the first paint widens every tab without resizing the row.
            globalThis.document?.fonts?.ready?.then?.(() => this.rp_layoutTabs());

            const onHistory = (event) => {
                const values = filtersToRestore(event.state, this.$wire.filters);

                if (values !== null) this.$wire.restoreFilters(values);
            };
            window.addEventListener('popstate', onHistory);
            unlistenHistory = () => window.removeEventListener('popstate', onHistory);
        },

        destroy() {
            unhook?.();
            unhook = null;
            unlistenHistory?.();
            unlistenHistory = null;
            unlistenInfo?.();
            unlistenInfo = null;
            infoRoot = null;
            stopWatchingDrilldown?.();
            stopWatchingDrilldown = null;
            stopWatchingTypeEditor?.();
            stopWatchingTypeEditor = null;
            stripObserver?.disconnect();
            stripObserver = null;
            observedStrip = null;
            window.removeEventListener('pointerdown', trackPointer, true);
            window.removeEventListener('pointermove', trackPointer, true);
            window.removeEventListener(DROP_CANCEL_EVENT, onDropCancel);
            this.rp_tabDragDestroy();
        },

        /**
         * One load's answer. A report's event only: two report pages never share a window, but a
         * held one may. And the open tab's only: an answer for a tab the page no longer shows would
         * reach hosts that are gone, and be held for them until a later visit replayed it. Dispatched
         * events are routed before the succeed callbacks run, so the tab the same response drew
         * (an empty tab answered at once) is caught up with first.
         */
        rp_receive(detail) {
            if (! detail || String(detail.reportId) !== reportId || ! detail.cards || typeof detail.cards !== 'object') return;

            this.rp_sync();

            if (String(detail.tabId ?? '') !== openTab) return;

            const next = { ...this.rp_cards };
            const parts = [];
            let failed = 0;

            for (const [cardId, card] of Object.entries(detail.cards)) {
                const part = hostPart(card);
                const slot = this.rp_slot(cardId);

                if (part?.status === 'failed') failed += 1;

                next[slot] = {
                    lines: [...this.rp_strings(card?.notes), ...this.rp_strings(card?.notApplied)],
                    missed: Number.isInteger(card?.missedFilters) && card.missedFilters > 0 ? card.missedFilters : 0,
                    warns: card?.warns === true,
                    applied: appliedFiltersOf(card?.filtersApplied),
                    // A key describes a chart; a card that now shows a sentence keeps none.
                    key: part?.contract ? keyOf(card.key, next[slot]?.key ?? []) : [],
                    // Kept until rp_send() sends the host something new: an unchanged contract is
                    // not sent again, so the chart and its table stay drawn.
                    drawn: next[slot]?.drawn ?? false,
                    // Whether it has a chart to expand, the periods that chart opens and its source
                    // link; a card showing a sentence has none of them to offer.
                    chart: Boolean(part?.contract),
                    actions: part?.contract ? actionsOf(card.actions) : [],
                    deeplink: part?.contract ? safeDeeplink(card.deeplink) : null,
                    vizType: part?.contract?.vizType ?? null,
                };

                if (part) parts.push([cardId, part, card.tooltip ?? null, next[slot].actions]);

                if (part?.contract) {
                    charts.set(cardId, { contract: part.contract, tooltip: card.tooltip ?? null });
                } else {
                    charts.delete(cardId);
                }
            }

            // Every footer first, then the hosts: Alpine prints the footers in the same flush that
            // swaps each host's skeleton for its chart box, and a chart draws only after that, once
            // its bundle and ECharts have mounted, so its row's height is already final. The ↗
            // and expand icons beside a card's heading appear in that flush too, in the places its
            // first paint kept for them.
            this.rp_cards = next;
            // A card left with nothing to list loses its ⓘ, and must not reopen its panel the next
            // time a load gives it something.
            if (this.rp_infoCard !== '' && ! hasInfo(next[this.rp_infoCard])) this.rp_infoClose(false);
            parts.forEach(([cardId, part, tooltip, actions]) => this.rp_send(cardId, part, tooltip, actions));

            // A partial load follows an arrange action, which has already said what it did; it
            // speaks only when a card it loaded failed.
            if (detail.partial === true) {
                if (failed > 0) this.rp_announce(text.loadedWithFailures ?? 'Report loaded. Some charts could not be loaded.');

                return;
            }

            this.rp_announce(this.rp_loadedSentence(detail, failed));
        },

        /**
         * What the status region says once a load is in: on a report of several tabs, the tab's
         * name, so a reader who opened one with the keyboard, whose focus stays on the tab, hears
         * that its cards are in; an empty tab says it has none. A report of one tab says "Report".
         */
        rp_loadedSentence(detail, failed) {
            const name = typeof detail.tabName === 'string' && detail.tabName !== '' ? detail.tabName : null;

            if (name === null) {
                return failed === 0 ? (text.loaded ?? 'Report loaded.') : (text.loadedWithFailures ?? 'Report loaded. Some charts could not be loaded.');
            }

            if (Object.keys(detail.cards).length === 0) return withTabName(text.tabEmpty ?? ':tab has no charts yet.', name);

            return withTabName(failed === 0 ? (text.tabLoaded ?? ':tab loaded.') : (text.tabLoadedWithFailures ?? ':tab loaded. Some charts could not be loaded.'), name);
        },

        /** The tab the server last drew, from the snapshot; '' where the page has none. */
        rp_drawnTab() {
            const drawn = this.$wire?.drawnTabId;

            return typeof drawn === 'string' ? drawn : '';
        },

        /**
         * Catches the scope up with the tab the server drew. When it changed, nothing of the old tab
         * is kept: the hosts' routers forget every id this page sent anything to, so no host of the
         * old tab replays its last detail if it mounts again on a later visit, and the comparison
         * and every footer start empty, so the new tab's cards are all sent what they are answered.
         * Run before Livewire morphs the new tab in, so its hosts start after the forgetting.
         */
        rp_sync() {
            const drawn = this.rp_drawnTab();

            if (drawn === openTab) return;

            openTab = drawn;

            if (touched.size > 0) {
                window.dispatchEvent(new CustomEvent(FORGET_EVENT, { detail: { ids: [...touched] } }));
            }

            touched.clear();
            sent.clear();
            charts.clear();
            this.rp_cards = {};
            this.rp_infoClose(false);
            // A name field left open belonged to the tab that was open.
            this.rp_tabRenaming = false;
            this.rp_revealOpenTab();
        },

        /**
         * A tab pressed, or Enter or Space on the focused one. The id is read off the button, never
         * written into an expression, and handed to Livewire as a value; the server looks it up
         * among this report's tabs and pushes it to the history. Pressing the open tab does nothing
         * unless another switch is out, which it then undoes. A switch that failed left `$wire.tab`
         * on the tab it asked for while the page still shows the old one, so pressing that tab
         * again asks again.
         */
        rp_openTab(event) {
            const tabId = event?.currentTarget?.getAttribute?.('data-report-tab');

            if (typeof tabId !== 'string' || (tabId === openTab && tabId === String(this.$wire.tab ?? ''))) return;

            this.$wire.$set('tab', tabId);
        },

        /**
         * Left/Right and Home/End move focus along the tablist; the tab under focus opens only when
         * pressed. Only the tabs the row shows take part: one rp_layoutTabs() moved under "More" is
         * not in the row to focus.
         */
        rp_tabKeydown(event) {
            const current = event?.currentTarget;
            const tabs = [...(current?.closest?.('[role="tablist"]')?.querySelectorAll('[role="tab"]') ?? [])]
                .filter((tab) => tab === current || ! tab.classList?.contains?.(TAB_HIDDEN));
            const next = tabIndexAfterKey(event?.key, tabs.indexOf(current), tabs.length);

            if (next === null) return;

            event.preventDefault();
            tabs[next].focus();
        },

        /**
         * The open tab brought into the row, on the first paint (a link to the ninth tab) and after
         * a switch by back or forward, once the morph has drawn it: the row is laid out again, and
         * the open tab is always in it.
         */
        rp_revealOpenTab() {
            requestAnimationFrame(() => this.rp_layoutTabs());
        },

        /**
         * Lays the tab row out (components/report/tab-strip.blade.php). The row does not scroll: the
         * tabs that do not fit beside the open one are hidden and listed under "More (N)"
         * (tabsToHide()), and the open tab's options and name field, which cannot sit inside the
         * tablist, are placed against it. Runs after every render and whenever the row changes size;
         * every tab is shown again to be measured and the answer applied before the browser paints,
         * so nothing flickers.
         */
        rp_layoutTabs() {
            const strip = pageRootOf(this)?.querySelector?.('[data-report-tab-strip]');

            if (! strip || typeof strip.getBoundingClientRect !== 'function') return;

            if (typeof ResizeObserver !== 'undefined' && observedStrip !== strip) {
                stripObserver?.disconnect();
                stripObserver = new ResizeObserver(() => this.rp_layoutTabs());
                stripObserver.observe(strip);
                observedStrip = strip;
            }

            if (! strip.querySelector('[data-report-tab-rename]')) this.rp_tabRenaming = false;

            const list = strip.querySelector('[role="tablist"]');
            const tabs = list ? [...list.querySelectorAll('[role="tab"]')] : [];
            const more = strip.querySelector('[data-report-tab-more]');
            const items = more ? [...more.querySelectorAll('[data-report-tab-more-item]')] : [];

            tabs.forEach((tab) => tab.classList.remove(TAB_HIDDEN));
            more?.classList.remove(TAB_HIDDEN);

            const hidden = new Set();

            if (list && tabs.length > 0) {
                const outer = (element) => {
                    const style = window.getComputedStyle(element);

                    return element.getBoundingClientRect().width + (parseFloat(style.marginLeft) || 0) + (parseFloat(style.marginRight) || 0);
                };
                const stripStyle = window.getComputedStyle(strip);
                const stripGap = parseFloat(stripStyle.columnGap) || 0;
                const add = strip.querySelector('[data-report-tab-control="add"]');
                const room = strip.clientWidth - (parseFloat(stripStyle.paddingLeft) || 0) - (parseFloat(stripStyle.paddingRight) || 0)
                    - (add ? outer(add) + stripGap : 0);
                const openIndex = tabs.findIndex((tab) => tab.getAttribute('aria-selected') === 'true');

                tabsToHide(tabs.map(outer), openIndex, room, parseFloat(window.getComputedStyle(list).columnGap) || 0, more ? outer(more) + stripGap : 0)
                    .forEach((index) => hidden.add(tabs[index].getAttribute('data-report-tab')));
            }

            tabs.forEach((tab) => tab.classList.toggle(TAB_HIDDEN, hidden.has(tab.getAttribute('data-report-tab'))));
            items.forEach((item) => item.classList.toggle(TAB_HIDDEN, ! hidden.has(item.getAttribute('data-report-tab-more-item'))));
            more?.classList.toggle(TAB_HIDDEN, hidden.size === 0);

            const count = more?.querySelector('[data-report-tab-more-count]');

            if (count) count.textContent = String(hidden.size);

            this.rp_placeTabControls(strip);

            if (focusOpenTabAfterLayout) {
                focusOpenTabAfterLayout = false;
                list?.querySelector('[role="tab"][aria-selected="true"]')?.focus();
            }
        },

        /**
         * The open tab's options and its name field over it: both are the row's children, positioned
         * against the tab (or, on a report of one tab, its heading), which keeps their room
         * (`data-report-tab-anchor`). The options' ⋯ sits inside the open tab's pill, in the room at
         * its right end, so the two read as one shape; beside a one-tab report's heading, just after it.
         */
        rp_placeTabControls(strip) {
            const anchor = strip.querySelector('[data-report-tab-anchor]');

            if (! anchor) return;

            const row = strip.getBoundingClientRect();
            const box = anchor.getBoundingClientRect();
            const left = box.left - row.left - (strip.clientLeft || 0);
            const options = strip.querySelector('[data-report-tab-options]');
            const rename = strip.querySelector('[data-report-tab-rename]');
            // The ⋯ is 28px wide (h-7 w-7); hidden while renaming, it measures 0.
            const inside = left + box.width - (options?.offsetWidth || 28) - 6;

            if (options) options.style.left = `${Math.round(anchor.getAttribute?.('role') === 'tab' ? inside : left + box.width + 4)}px`;
            if (rename) rename.style.left = `${Math.round(left)}px`;
        },

        /**
         * A tab chosen under "More": opened as a pressed tab is. The item leaves the list once the
         * tab is open, so focus goes to the tab's own button in the row when it is laid out again.
         */
        rp_openTabFromMore(el) {
            const tabId = el?.getAttribute?.('data-report-tab-more-item');

            if (typeof tabId !== 'string' || tabId === '') return;

            focusOpenTabAfterLayout = true;
            this.$wire.$set('tab', tabId);
        },

        /** The options' Rename: the name field over the open tab, its text selected. */
        rp_startRename() {
            this.rp_tabRenaming = true;
            this.$nextTick(() => {
                this.rp_layoutTabs();
                const field = this.rp_tabControl('name');

                field?.focus?.();
                field?.select?.();
            });
        },

        /** Escape or the cross: the name as it was, the field closed, and focus back on the tab's options. */
        rp_cancelRename() {
            const field = pageRootOf(this)?.querySelector?.('[data-report-tab-control="name"]');

            if (field) field.value = field.defaultValue;

            this.rp_tabRenaming = false;
            this.$nextTick(() => {
                this.rp_layoutTabs();
                this.rp_tabControl('options')?.focus?.();
            });
        },

        /** Enter or the check: the name typed goes to the server, which keeps the field open if it refuses it. */
        rp_submitRename(form) {
            const field = form?.querySelector?.('[data-report-tab-control="name"]');

            this.$wire.renameTab(this.rp_tabOf(form), field?.value ?? '');
        },

        /**
         * The name field left for something else on the page, by a click or Tab: the name typed is
         * taken as Enter takes it, but focus stays where it went (Show::renameTab()'s `$isLeaving`),
         * and the field closes now. Escape and the cross close the field before focus leaves it, and
         * Enter's answer does, so none of them gets here; nor does another window taking focus.
         */
        rp_leaveRename(event) {
            const form = event?.currentTarget;

            if (! this.rp_tabRenaming || ! form || form.contains(event.relatedTarget) || globalThis.document?.hasFocus?.() === false) return;

            const field = form.querySelector?.('[data-report-tab-control="name"]');

            this.rp_tabRenaming = false;
            this.$nextTick(() => this.rp_layoutTabs());
            this.$wire.renameTab(this.rp_tabOf(form), field?.value ?? '', true);
        },

        rp_send(cardId, part, tooltip, actions = []) {
            const id = hostIdFor(reportId, cardId);
            const periodClicks = Boolean(part.contract) && opensOnClick(actions);
            const compared = JSON.stringify({ ...part, tooltip, periodClicks });

            if (sent.get(id)?.compared === compared) return;
            // A drawn table is kept through a reload as a drawn chart is: both answer from what they hold.
            sent.set(id, { compared, drawsChart: Boolean(part.contract || part.table) });
            // Cleared before the host is sent it: the host takes its old chart and table down to
            // draw the new one, or shows a sentence, and only its next draw puts a table back.
            this.rp_setDrawn(cardId, false);

            send(part.contract
                ? this.rp_chartDetail(id, part.contract, tooltip, periodClicks, periodClicks ? (instant) => this.rp_clickHint(cardId, instant) : null)
                : { id, ...part });
        },

        /**
         * What a chart host is sent to draw a contract: a card's own, its expanded view's copy and
         * the type editor's preview all go through here, so the three draw it alike. The tooltip is
         * built here, since the host takes one only from script.
         */
        rp_chartDetail(id, contract, tooltip, periodClicks, hint) {
            return {
                id,
                contract,
                tooltip: tooltipFor(contract, tooltip, {
                    locale: document.documentElement.lang || undefined,
                    text: text.tooltip ?? {},
                    hint,
                }),
                // Sent even when null: the host keeps the last names it was given, and a card that no
                // longer names its periods must not read out the ones it named before.
                periods: periodsOf(tooltip),
                // Sent even when null, as `periods` is: the host keeps the last colours it was given.
                styleColors: styleColorsFor(tooltip),
                // Sent even when false: the host keeps the last it was given, and a card that no
                // longer opens a period must stop reporting clicks on it.
                periodClicks,
            };
        },

        /**
         * The tooltip's last line under a period a mouse click opens, and none under any other or
         * when the tooltip was opened by a touch or a pen, whose tap opens nothing. On the card that
         * is its expanded view (`clickHint`); on the expanded copy, the narrowed records (`narrowHint`).
         */
        rp_clickHint(cardId, instant, key = 'clickHint') {
            if (lastPointer !== CLICKING_POINTER) return null;

            const action = (this.rp_cards[this.rp_slot(cardId)]?.actions ?? []).find((entry) => entry.instant === Number(instant));

            if (! action?.drilldown) return null;

            return key === 'narrowHint'
                ? (text.tooltip?.narrowHint ?? "Click to show this period's records")
                : (text.tooltip?.clickHint ?? 'Click to open details');
        },

        /**
         * A click the host reported on a card's chart: the period the tooltip showed (`instant`), or
         * a null instant for a click off the plot, which only a Big Number answers, with its figure's
         * period, the headline, which is the newest and so first. A mouse click only.
         */
        rp_periodClick(event) {
            const id = event?.detail?.id;

            if (event?.detail?.pointerType !== CLICKING_POINTER) return;

            // The expanded view's copy narrows the records it shows; a card's own chart opens them.
            const narrowing = id === expandedHostId && expandedCardId !== null;
            const cardId = narrowing ? expandedCardId : cardIdOf(id);

            if (cardId === null) return;

            const action = this.rp_actionAt(this.rp_cards[this.rp_slot(cardId)], event.detail.instant);

            if (action?.drilldown) this.rp_openExpanded(cardId, action, narrowing);
        },

        /** The period at a clicked instant; a click off the plot is a Big Number's figure, its headline. */
        rp_actionAt(card, instant) {
            return instant === null || instant === undefined
                ? figureAction(card)
                : (card?.actions ?? []).find((entry) => entry.instant === Number(instant));
        },

        /** Whether a click on the card an element sits in opens something off its plot: a Big Number's figure. */
        rp_opensFigure(el) {
            return Boolean(figureAction(this.rp_cards[this.rp_slot(this.rp_cardOf(el))])?.drilldown);
        },

        /** A card's expand icon: its expanded view, on the whole span it draws. */
        rp_expand(el) {
            this.rp_openExpanded(this.rp_cardOf(el));
        },

        /**
         * Opens a card's expanded view, or narrows the open one: DrilldownTable's modal with a copy
         * of the card's chart above the records of `action`'s period, or of the card's whole span
         * with none. Opening sends the copy's host the card's chart first, which the host router
         * hands over when the modal draws the host; narrowing only reads the records again.
         *
         * When the modal closes, x-trap returns focus to what held it as it opened: the pressed
         * expand icon, or the chart's figure, which a click on the chart focuses (it is focusable,
         * tabindex -1). Where that leaves focus nowhere (Safari focuses no pressed button, and a card
         * drawn again meanwhile takes the old figure with it), it goes to the card's expand icon.
         */
        rp_openExpanded(cardId, action = null, narrowing = false) {
            const card = this.rp_cards[this.rp_slot(cardId)];
            const chart = charts.get(cardId);

            if (! card?.chart || ! chart) return;

            const actions = card.actions ?? [];
            const span = spanOf(actions);
            const shown = action?.drilldown ?? span;

            if (! narrowing) {
                send(this.rp_chartDetail(expandedHostId, chart.contract, chart.tooltip, actions.length > 0,
                    actions.length > 0 ? (instant) => this.rp_clickHint(cardId, instant, 'narrowHint') : null));
                expandedCardId = cardId;
                stopWatchingDrilldown?.();
                stopWatchingDrilldown = returnFocusAfterDrilldown(() => this.rp_expandIconFor(cardId));
            }

            window.Livewire?.dispatch(DRILLDOWN_EVENT, {
                metricId: shown?.metricId ?? 0,
                metricName: shown?.metricName ?? this.rp_cardTitleFor(cardId),
                startDate: shown?.startDate ?? '',
                endDate: shown?.endDate ?? '',
                chartId: expandedHostId,
                spanStartDate: span?.startDate ?? '',
                spanEndDate: span?.endDate ?? '',
                periodName: action?.name ?? '',
                hasRecords: shown !== null,
            });
        },

        /** The last grid row name pressed; an answer for an earlier press opens nothing. */
        rp_gridRowAsk: 0,

        /**
         * A scorecard grid row's name pressed (superset-table.js's `superset-table-row-open`):
         * the expanded view a metric card's expand icon opens, for the row's metric over the
         * grid's span. The row's chart is read only now (Show::gridRowChart(), a Line card's
         * contract under the grid's filters, and the modal's event), then kept beside the cards'
         * under gridRowChartId(), so a click on one of the copy's periods narrows the records
         * through rp_periodClick() as a card's does. A row with no records to open shows its
         * chart alone. When the modal closes, focus goes back to the row's name if it landed
         * nowhere. A row the server says opens nothing, or a read that failed, says so instead.
         */
        async rp_gridRowOpen(event) {
            const detail = event?.detail;
            const cardId = cardIdOf(detail?.id);

            if (cardId === null || typeof detail.rowKey !== 'string') return;

            const ask = ++this.rp_gridRowAsk;
            const answer = await this.$wire.gridRowChart(cardId, detail.rowKey);

            if (ask !== this.rp_gridRowAsk || ! answer || typeof answer !== 'object') return;

            const drilldown = gridRowDrilldown(answer.drilldown);

            if (! answer.contract || typeof answer.contract !== 'object' || drilldown === null) {
                if (typeof answer.emptyMessage === 'string' && answer.emptyMessage !== '') this.rp_announce(answer.emptyMessage);

                return;
            }

            const rowId = gridRowChartId(cardId, detail.rowKey);
            const tooltip = answer.tooltip ?? null;
            const actions = actionsOf(answer.actions);

            charts.set(rowId, { contract: answer.contract, tooltip });
            this.rp_cards = {
                ...this.rp_cards,
                [this.rp_slot(rowId)]: { lines: [], key: [], drawn: false, chart: true, actions, deeplink: null, vizType: answer.contract.vizType ?? null },
            };

            send(this.rp_chartDetail(expandedHostId, answer.contract, tooltip, actions.length > 0,
                actions.length > 0 ? (instant) => this.rp_clickHint(rowId, instant, 'narrowHint') : null));
            expandedCardId = rowId;
            stopWatchingDrilldown?.();
            stopWatchingDrilldown = returnFocusAfterDrilldown(() => (typeof detail.findTrigger === 'function' ? detail.findTrigger() : null));

            window.Livewire?.dispatch(DRILLDOWN_EVENT, { ...drilldown, chartId: expandedHostId });
        },

        /**
         * A grid row's ✕ while the page edits rows (`superset-table-row-remove`): the row's metric
         * out of the grid, exactly as the arrange strip's Rows menu takes it out (Show::removeRow()).
         */
        rp_gridRowRemove(event) {
            const cardId = cardIdOf(event?.detail?.id);
            const metricId = event?.detail?.metricId;

            if (cardId === null || ! Number.isInteger(metricId)) return;

            this.$wire.removeRow(cardId, metricId);
        },

        /**
         * A grid row dropped, or stepped by the arrow keys on its handle (`superset-table-row-move`):
         * Show::moveGridRow() moves it in the working copy, says where it went and loads the grid
         * again; the table host keeps focus on the row's handle through that draw.
         */
        rp_gridRowMove(event) {
            const cardId = cardIdOf(event?.detail?.id);
            const { rowKey, toIndex } = event?.detail ?? {};

            if (cardId === null || typeof rowKey !== 'string' || ! Number.isInteger(toIndex)) return;

            this.$wire.moveGridRow(cardId, rowKey, toIndex);
        },

        /**
         * Move up or Move down in a grid's Rows menu (edit-toolbar.blade.php): the pointer's way to
         * reorder rows without dragging (WCAG 2.5.7), through Show::moveGridRow() as a drag is. The
         * row is read off the button (`data-row-key`, `data-row-index`), never written into an
         * expression. Once the page has redrawn the menu, focus goes back to the same step of the
         * moved row, or its other step when that one is now disabled at an end, else the Rows button.
         */
        rp_gridRowStep(el, delta) {
            const cardId = this.rp_cardOf(el);
            const rowKey = el?.getAttribute?.('data-row-key');
            const index = Number(el?.getAttribute?.('data-row-index'));
            const step = el?.getAttribute?.('data-grid-row-step');

            if (cardId === '' || typeof rowKey !== 'string' || ! Number.isInteger(index) || ! Number.isInteger(delta)) return;

            pendingRowStep = { cardId, rowKey, step };
            this.$wire.moveGridRow(cardId, rowKey, index + delta);
        },

        /** Whether the card an element sits in has a chart to expand: any card its load sent one. */
        rp_expandableFor(el) {
            return this.rp_cards[this.rp_slot(this.rp_cardOf(el))]?.chart === true;
        },

        /** The name a card's heading prints, for the expanded view of a card whose periods name none. */
        rp_cardTitleFor(cardId) {
            return this.rp_cardElementFor(cardId)?.querySelector('[data-card-heading]')?.textContent?.trim() ?? '';
        },

        /**
         * A card's pen, or while editing its Type control: the type editor opens here and now, not on
         * the server's answer. The dialog shows at once, named for the card and set to its type (read
         * off the button), and its preview draws the card's own chart, which the page already holds,
         * on the next frame, once the host is on screen. The server is only told which card it is
         * changing, and asked for a preview only for a card the page drew no chart for. Focus comes
         * back to the button when the dialog closes; a narrow card's Type sits in its "⋯" panel,
         * which closed when focus went into the dialog, so it is opened again first.
         */
        rp_editType(el) {
            const cardId = this.rp_cardOf(el);

            if (cardId === '') return;

            const returnTo = el?.closest?.('[data-arrange-strip]') ? '[data-arrange-control="type"]' : '[data-report-card-edit]';

            typeEditorCard = { id: cardId, type: el?.getAttribute?.('data-card-type') ?? '' };
            this.rp_typeEditorTitle = this.rp_cardTitleFor(cardId);
            this.rp_typeEditorChoices = typeChoicesOf(el?.getAttribute?.('data-type-choices'));
            this.rp_typeEditorChoice = typeEditorCard.type;
            stopWatchingTypeEditor?.();
            stopWatchingTypeEditor = returnFocusAfterModal(TYPE_EDITOR_ID, () => {
                const back = this.rp_cardElementFor(cardId)?.querySelector(returnTo) ?? null;

                if (back?.offsetParent !== null) return back;

                this.rp_focusRevealed(back);

                return null;
            });

            const drawn = charts.has(cardId);

            this.$wire.$set('isTypeEditorOpen', true, false);
            if (drawn) requestAnimationFrame(() => this.rp_previewOwnChart());
            this.$wire.openTypeEditor(cardId, ! drawn);
        },

        /**
         * A type chosen in the editor. The card's own type draws from what the page holds; any other
         * shows the preview's skeleton while the server draws the card as that type.
         */
        rp_previewType(type) {
            if (typeof type !== 'string' || type === '' || typeEditorCard === null) return;

            this.rp_typeEditorChoice = type;

            if (type === typeEditorCard.type && charts.has(typeEditorCard.id)) {
                this.rp_previewOwnChart();

                return;
            }

            send({ id: previewHostId, status: 'loading' });
            this.$wire.previewCardType(type);
        },

        /** The card being changed, drawn in the preview as the page last drew it, with nothing to click. */
        rp_previewOwnChart() {
            const chart = charts.get(typeEditorCard?.id);

            if (chart) send(this.rp_chartDetail(previewHostId, chart.contract, chart.tooltip, false, null));
        },

        /**
         * A type editor preview from the server (`report-card-preview`): the card drawn as `type`, sent
         * to the dialog's host as a load sends a card's, with nothing to click. This report's preview
         * host only, and only while `type` is still the one chosen: a slower answer for a type the
         * reader has since moved off never draws over the one they are looking at.
         */
        rp_preview(detail) {
            if (! detail || String(detail.reportId) !== reportId || detail.hostId !== previewHostId || detail.type !== this.rp_typeEditorChoice) return;

            const part = hostPart(detail.card);

            if (! part || part.table) return;

            send(part.contract ? this.rp_chartDetail(previewHostId, part.contract, detail.card.tooltip ?? null, false, null) : { id: previewHostId, ...part });
        },

        /** A card's expand icon. */
        rp_expandIconFor(cardId) {
            return this.rp_cardElementFor(cardId)?.querySelector('[data-report-card-expand]') ?? null;
        },

        /** A card's element, found by walking the cards rather than by a selector built from a stored id. */
        rp_cardElementFor(cardId) {
            for (const element of pageRootOf(this)?.querySelectorAll?.('[data-site-analytics-card]') ?? []) {
                if (element.getAttribute('data-site-analytics-card') === cardId) return element;
            }

            return null;
        },

        /** The periods the card an element sits in opens, newest first: none hides its expand icon. */
        rp_actionsFor(el) {
            return this.rp_cards[this.rp_slot(this.rp_cardOf(el))]?.actions ?? [];
        },

        /** The source link the card an element sits in opens, or null, which hides its ↗ icon. */
        rp_sourceFor(el) {
            return this.rp_cards[this.rp_slot(this.rp_cardOf(el))]?.deeplink ?? null;
        },

        /**
         * Every host that shows no chart goes back to loading before a load, through the same
         * comparison, so the answer is always sent to it, even one identical to the last. A host
         * drawing a chart keeps it until its answer lands, and redraws only if it changed. A partial
         * load touches only the hosts of the cards it asks for.
         */
        rp_begin(cardIds = null) {
            const only = cardIds === null ? null : new Set(cardIds.map((cardId) => hostIdFor(reportId, cardId)));

            for (const [id, record] of sent) {
                if (record === LOADING || record.drawsChart || (only !== null && ! only.has(id))) continue;

                sent.set(id, LOADING);
                send({ id, status: 'loading' });
            }
        },

        /**
         * A load nothing answered: every host still waiting on it fails, with its Retry, rather than
         * showing a skeleton forever. A host drawing a chart keeps it. Found in the page, because
         * after a failed first load no host has been sent anything yet.
         */
        rp_failWaiting() {
            for (const id of this.rp_cardHostIds()) {
                const record = sent.get(id);

                if (record !== undefined && record !== LOADING) continue;

                sent.delete(id);
                send({ id, status: 'failed' });
            }

            this.rp_announce(text.loadFailed ?? 'The report could not be loaded.');
        },

        /** Every card host in the page by its id; the expanded view's and the type editor's are not a card's. */
        rp_cardHostIds() {
            const ids = [];

            for (const element of this.$root.querySelectorAll(HOST_ATTRIBUTES.map((name) => `[${name}]`).join(', '))) {
                const id = HOST_ATTRIBUTES.map((name) => element.getAttribute(name)).find((value) => typeof value === 'string') ?? null;

                if (cardIdOf(id) !== null) ids.push(id);
            }

            return ids;
        },

        /**
         * Apply filters or Reset pressed (the filter bar's `report-filters-applying`), before the
         * round trip: every card on the tab goes back to its skeleton at once, a drawn chart or grid
         * included, so nothing drawn from the old values stays up under the new ones. Each is recorded
         * as loading, so its answer is always sent and drawn as on first paint, even one identical to
         * the last. Found in the page as well as in what was sent, so a card showing a failure goes
         * back to loading too. Until the answer lands a card offers nothing its old chart held: no
         * expand or source link, and its key keeps each name and swatch but not its figure, so the
         * card keeps its height and no old figure is left printed. Said once in the status region;
         * the load that follows says when the cards are in, and a call that fails fails every card
         * still waiting (FILTER_METHODS).
         */
        rp_filtersApplying() {
            for (const id of new Set([...this.rp_cardHostIds(), ...sent.keys()])) {
                if (sent.get(id) === LOADING) continue;

                sent.set(id, LOADING);
                send({ id, status: 'loading' });
            }

            charts.clear();
            this.rp_cards = Object.fromEntries(Object.entries(this.rp_cards).map(([slot, card]) => [slot, {
                ...card,
                key: (card.key ?? []).map((entry) => ({ ...entry, value: null })),
                drawn: false,
                chart: false,
                actions: [],
                deeplink: null,
                vizType: null,
            }]));
            this.rp_announce(text.filtersApplying ?? 'Loading filtered results.');
        },

        /**
         * Whether the filter bar's controls hold anything the cards were not drawn with
         * (filtersPending()): the bar then says "Changes not applied" and rings Apply. Read off
         * `$wire`, whose values change as a control does, so it follows every pick at once, and
         * whose applied values land with the round trip of Apply, Reset, Back or Forward.
         */
        rp_filtersPending() {
            return filtersPending(this.$wire?.pending, this.$wire?.appliedBar);
        },

        /**
         * Run by the bar's status line (its x-effect) whenever a control or the applied values
         * change. The status region says so once as the bar turns pending, not again on every pick
         * after, and says nothing as it turns back: Apply and Reset already say the cards are loading
         * and then that they are in, and the line itself, which Apply is described by, says the rest.
         */
        rp_filtersChanged() {
            const pending = this.rp_filtersPending();

            if (pending && ! filtersWerePending) this.rp_announce(text.filtersPending ?? 'Filter changes not applied yet.');

            filtersWerePending = pending;
        },

        /**
         * A card's Retry, bubbled from its host once the host has parked focus on the card's heading.
         * The tab loads again as a whole, because a tab's metric cards are read in one batch.
         */
        rp_retry(event) {
            const id = event?.detail?.id;

            // The type editor's preview failed to read: the server reads the same choice again.
            if (id === previewHostId) {
                this.rp_previewType(this.rp_typeEditorChoice);
                return;
            }

            if (cardIdOf(id) === null) return;

            // The host has already shown its skeleton; recorded so its answer is never "unchanged".
            sent.set(id, LOADING);
            this.rp_setDrawn(cardIdOf(id), false);
            this.rp_reload();
        },

        /**
         * Load the tab again, or only these of its cards. A whole-tab load already out answers a
         * second whole-tab request (a Retry pressed twice). Anything else asked while a load is out
         * is queued and asked once that load settles: a card added a moment ago was not on the tab
         * when the load out was asked, so dropping the request would leave it on its skeleton.
         */
        rp_reload(cardIds = null) {
            if (loading) {
                // A whole-tab load out already answers a whole-tab request (a Retry pressed twice).
                if (cardIds === null && out === null) return;

                queued = queuedLoad(queued, cardIds);

                return;
            }

            loading = true;
            out = cardIds;
            this.rp_begin(cardIds);

            if (cardIds === null) {
                this.$wire.loadTab();
            } else {
                this.$wire.loadTab(cardIds);
            }
        },

        rp_flushQueued() {
            if (queued === undefined) return;

            const next = queued;
            queued = undefined;
            this.rp_reload(next);
        },

        /**
         * One arrange action's outcome: its sentence, the cards it changed loaded again, and focus
         * moved once the morph that came with it has landed.
         */
        rp_arrange(detail) {
            if (! detail || String(detail.reportId) !== reportId) return;

            // Caught up first with a tab the same response opened (Add tab, Duplicate), as
            // rp_receive() is: events are routed before the succeed callbacks, and the switch there
            // would otherwise close the name field this event opens for the new tab.
            this.rp_sync();

            // Whatever the action was, it has answered. A palette still open after it (a click-add
            // the server refused) shows every entry again rather than the one that was pressed.
            this.rp_palHolding = false;

            if (typeof detail.message === 'string' && detail.message !== '') {
                // The action's sentence says what happened; a count still waiting would talk over it.
                clearTimeout(palAnnounceTimer);
                this.rp_arrangeAnnounce(detail.message);
            }

            const reload = Array.isArray(detail.reload) ? detail.reload.filter((id) => typeof id === 'string') : [];

            // Once the response has landed, as the server's own loads are (Show::requestAfterLanding()):
            // asked for now, the load would go out before the morph starts the child components the
            // response added (a dataset card's data source picker), and Livewire would throw
            // "Component not found" walking them.
            if (reload.length > 0) setTimeout(() => this.rp_reload(reload), 0);

            if (detail.focus && typeof detail.focus === 'object') {
                // The tab's name field is open while a name is being given (a refused rename, a new
                // tab) and closed once focus goes back to a tab or out of edit mode.
                if (detail.focus.target === 'tab-control' && detail.focus.control === 'name') {
                    this.rp_tabRenaming = true;
                } else if (detail.focus.target === 'tab' || detail.focus.target === 'start') {
                    this.rp_tabRenaming = false;
                }

                requestAnimationFrame(() => {
                    if (detail.focus.target === 'tab' || detail.focus.target === 'tab-control') this.rp_layoutTabs();

                    this.rp_focusRevealed(this.rp_focusTarget(detail.focus), detail.focus.select === true);
                });
            }

            // A Rows menu step's answer (moveGridRow sends no focus instruction, the table keeping
            // focus on a dragged row's handle): back to the pressed step of the moved row.
            if (pendingRowStep !== null) {
                const pending = pendingRowStep;
                pendingRowStep = null;
                requestAnimationFrame(() => this.rp_focusRowStep(pending));
            }
        },

        /**
         * Focus an element a focus instruction named, opening the disclosure it sits in first when
         * that is closed, so focus is never sent to a control nobody can see.
         */
        rp_focusRevealed(target, select = false) {
            focusRevealed(target, select);
        },

        /** The pressed step of a moved grid row in its Rows menu, else its other step, else the Rows button. */
        rp_focusRowStep({ cardId, rowKey, step }) {
            const card = this.rp_cardElementFor(cardId);
            const steps = [...(card?.querySelectorAll?.('[data-grid-row-step]') ?? [])]
                .filter((button) => button.getAttribute('data-row-key') === rowKey && ! button.disabled);

            this.rp_focusRevealed(steps.find((button) => button.getAttribute('data-grid-row-step') === step)
                ?? steps[0]
                ?? card?.querySelector?.('[data-arrange-control="rows"]')
                ?? null);
        },

        /** A card's data source was saved or disconnected in its picker: that card's figures again. */
        rp_sourceChanged(detail) {
            if (! detail || String(detail.reportId) !== reportId || typeof detail.cardId !== 'string') return;

            this.rp_reload([detail.cardId]);
        },

        /**
         * The element a focus instruction names, found in the page by data attribute, never by a
         * selector built from a card id: `start` (Arrange), `palette-trigger`, `add-tile` (the tile
         * after the grid that opens the Add chart panel), `palette` (the
         * palette's first control), `palette-entry` (the Add of the panel entry `key` names, or
         * the nearest one that can take focus; see paletteAddAfter()), `card` (a card's heading),
         * `control` (a control in a card's edit toolbar, else that card's heading when the control
         * is gone or disabled), `tab` (the row's tab `tabId` names when given and shown, else the open
         * tab's button, else its options button when there is no tablist), `tab-control` (an edit
         * control of the tab row; see rp_tabControl()) or `undo`
         * (the edit bar's Undo).
         */
        rp_focusTarget(focus) {
            const root = pageRootOf(this);

            switch (focus.target) {
                case 'tab':
                    // A `tabId` (a dragged tab, the tab a card was dropped on) names a tab of the row,
                    // matched by attribute value; one under "More" hands focus to the open tab.
                    return (typeof focus.tabId === 'string'
                        ? [...(root?.querySelectorAll?.('[role="tab"]') ?? [])].find((tab) => tab.getAttribute('data-report-tab') === focus.tabId && ! tab.classList.contains(TAB_HIDDEN))
                        : null)
                        ?? root?.querySelector?.('[role="tab"][aria-selected="true"]')
                        ?? this.rp_tabControl('options');
                case 'tab-control':
                    return this.rp_tabControl(focus.control);
                case 'undo':
                    return root?.querySelector?.('[data-report-undo]') ?? null;
                case 'start':
                    return root?.querySelector?.('[data-report-arrange-start]') ?? null;
                case 'palette-trigger':
                    return root?.querySelector?.('[data-report-palette-trigger]') ?? null;
                case 'add-tile':
                    // The "Add a chart to <tab>" tile after the grid; the header's Add chart once
                    // the tab has no cards and so no tile.
                    return root?.querySelector?.('[data-report-add-tile]')
                        ?? root?.querySelector?.('[data-report-palette-trigger]')
                        ?? null;
                case 'palette':
                    // The search first, asked for on its own: one comma-separated selector matches
                    // the first element in document order, not the first selector listed. The
                    // panel always draws its search; the chain after it is only a fallback.
                    return root?.querySelector?.('[data-report-palette-search]')
                        ?? root?.querySelector?.('[data-report-palette] input:checked, [data-report-palette] input, [data-report-palette] button')
                        ?? null;
                case 'palette-entry':
                    // The card the add made (or the grid a row went into) is shown to the editor
                    // without taking focus from the panel (rp_palShowAdded()).
                    this.rp_palShowAdded(focus.cardId);

                    // Matched by attribute value, never a selector built from the key. Focus stays
                    // in the panel: the search when no entry can take it.
                    return paletteAddAfter([...(root?.querySelectorAll?.('[data-report-palette] [data-palette-key]') ?? [])], focus.key)
                        ?? root?.querySelector?.('[data-report-palette-search]')
                        ?? null;
                case 'card':
                case 'control': {
                    const card = [...(root?.querySelectorAll?.('[data-site-analytics-card]') ?? [])]
                        .find((element) => element.getAttribute('data-site-analytics-card') === focus.cardId);
                    const heading = card?.querySelector?.('[data-card-heading]') ?? null;

                    if (focus.target === 'card' || ! ARRANGE_CONTROLS.has(focus.control)) return heading;

                    const controls = [...(card?.querySelectorAll?.('[data-arrange-control]') ?? [])];
                    const find = (name) => controls.find((element) => element.getAttribute('data-arrange-control') === name && ! element.disabled) ?? null;

                    return find(focus.control)
                        ?? (CONTROL_FALLBACK[focus.control] ? find(CONTROL_FALLBACK[focus.control]) : null)
                        ?? heading;
                }
                default:
                    return null;
            }
        },

        /**
         * An edit control of the tab row, by its data-report-tab-control name. One that is disabled
         * (a move button at an end) hands focus to the other move button, then to the name field, so
         * focus is never left on a control that cannot take it. An unknown name is the name field.
         */
        rp_tabControl(name) {
            const controls = [...(pageRootOf(this)?.querySelectorAll?.('[data-report-tab-control]') ?? [])];
            const find = (wanted) => controls.find((element) => element.getAttribute('data-report-tab-control') === wanted && ! element.disabled) ?? null;
            const wanted = TAB_CONTROLS.has(name) ? name : 'name';

            return find(wanted) ?? (TAB_CONTROL_FALLBACK[wanted] ? find(TAB_CONTROL_FALLBACK[wanted]) : null) ?? find('name');
        },

        /** The open tab's id for an edit control of the tab row; read off it, never written into an expression. */
        rp_tabOf(el) {
            return el?.closest?.('[data-report-tab-controls]')?.getAttribute('data-report-tab-controls') ?? '';
        },

        /**
         * The palette opened (its root's x-init, which also runs when it switches between the whole
         * tab and Add row mode, since its element is keyed by mode): it starts showing everything,
         * Metrics open, no "Add as" menu. Kept through the re-renders while it stays open, since
         * x-init does not run again on a morphed element.
         */
        rp_palReset() {
            this.rp_palQuery = '';
            this.rp_palSources = [];
            this.rp_palSourcesOpen = false;
            this.rp_palHolding = false;
            this.rp_palGone = false;
            this.rp_palSections = { metrics: true, datasets: false, layout: false };
            this.rp_palAsKey = '';
            clearTimeout(palAnnounceTimer);
        },

        /**
         * Keeps the fixed Add chart panel (lg up) flush with the top of what the page shows: right
         * under the app's top bar while that is in view, then on the scroll area's top edge, where edit
         * mode's bar pins (sticky, top-0), so no band of page shows above the panel at any scroll
         * position. Measured off the layout rather than assumed, and written as --report-palette-top on
         * the document element, as the edit bar's height is, since each render puts the panel's own
         * attributes back. Its listeners end themselves once this panel element is gone.
         */
        rp_palPin(el) {
            pinPanelTop(el, '--report-palette-top');
        },

        /**
         * Something was grabbed to drag (site-analytics-arrange.js's `arrange-grab`). Any grab closes
         * the panel's "Add as" menu, as every arrange menu closes. When it is one of the panel's own
         * entries, the panel shrinks to that entry, hidden in place. It cannot hide the entry: the
         * browser only drags an element it draws. A grab anywhere else leaves the panel alone:
         * closing it would move the page under the pointer before that drag starts.
         */
        rp_palGrabbed(palette, item) {
            this.rp_palAsKey = '';
            this.rp_palSourcesOpen = false;
            palDropCancelled = false;

            if (palette?.contains?.(item)) this.rp_palHolding = true;
        },

        /**
         * The held entry has left the palette (SortableJS's `start` on its list): the palette gets out
         * of the way, so the grid comes up under the pointer. Hidden in the browser only. The page
         * keeps it until the server closes it, since the dragged entry still lives inside it.
         */
        rp_palDragged() {
            this.rp_palGone = true;
        },

        /**
         * The press on an entry was let go (SortableJS's `unchoose`). Without a drag there is no
         * `end` and no add to answer, so the panel comes back whole here; once the entry was dragged
         * away, the drag's `end` closes the panel instead. An entry's Add and its "Add as" chevron
         * match the lists' SortableJS filter, so pressing either grabs nothing and never shrinks it.
         */
        rp_palReleased() {
            if (! this.rp_palGone) this.rp_palHolding = false;
        },

        /**
         * The drag is over (SortableJS's `end`), whether the entry was dropped on the grid or let go
         * elsewhere: the server closes the palette. A drop's own addCardAt() closes it too, and
         * closePalette() is idempotent, so the two can arrive together. The one exception is an entry
         * let go over the pinned edit bar or filter card, which the drag put back into the panel
         * (onDropCancel): nothing was added, so the panel comes back whole for another try.
         */
        rp_palDropped() {
            if (palDropCancelled) {
                palDropCancelled = false;
                this.rp_palHolding = false;
                this.rp_palGone = false;

                return;
            }

            this.$wire.closePalette();
        },

        /** Whether a palette entry matches the search. Its source is its group's to check. */
        rp_palTextShows(el) {
            const query = this.rp_palQuery;

            if (query !== palQueryFor) {
                palQueryFor = query;
                palWords = searchWords(query);
            }

            if (palWords.length === 0) return true;

            const raw = el?.getAttribute?.('data-palette-search') ?? '';
            let folded = palFolded.get(raw);

            if (folded === undefined) {
                folded = foldForSearch(raw);
                palFolded.set(raw, folded);
            }

            return palWords.every((word) => folded.includes(word));
        },

        /** Whether a source's group shows: the source filter lets it through and an entry of it matches. */
        rp_palGroupShows(el) {
            if (! this.rp_palSourceShows(el)) return false;

            return [...(el?.querySelectorAll?.('[data-palette-search]') ?? [])].some((entry) => this.rp_palTextShows(entry));
        },

        rp_palSourceShows(group) {
            const chosen = this.rp_palSources;

            return chosen.length === 0 || chosen.includes(group?.getAttribute?.('data-palette-source'));
        },

        /** Whether a dataset chart entry shows: it carries its own source, having no group. */
        rp_palEntryShows(el) {
            return this.rp_palSourceShows(el) && this.rp_palTextShows(el);
        },

        /** How many dataset charts the panel shows, of how many it lists. */
        rp_palDatasetCounts() {
            const entries = [...(this.$root?.querySelectorAll?.(PALETTE_DATASETS) ?? [])];

            return { shown: entries.filter((entry) => this.rp_palEntryShows(entry)).length, total: entries.length };
        },

        /** The count beside a section's heading: `metrics` or `datasets`. */
        rp_palShownText(section) {
            const { shown, total } = section === 'datasets' ? this.rp_palDatasetCounts() : this.rp_palCounts();

            return paletteShownText(text, shown, total);
        },

        rp_palDatasetsEmpty() {
            const { shown, total } = this.rp_palDatasetCounts();

            return total > 0 && shown === 0;
        },

        /**
         * The Sources control (a disclosure of toggle buttons, not a menu): Enter or Space on its
         * button opens the list and puts focus on its first option; Escape, or a click away, closes
         * it and focus goes back to the button.
         */
        rp_palSourcesToggle() {
            this.rp_palAsKey = '';
            this.rp_palSourcesOpen = ! this.rp_palSourcesOpen;

            if (this.rp_palSourcesOpen) {
                this.rp_palWhenShown('[data-palette-source-option]', (option) => option.focus?.());
            }
        },

        rp_palSourcesClose(returnFocus = false) {
            if (! this.rp_palSourcesOpen) return false;

            this.rp_palSourcesOpen = false;

            if (returnFocus) this.$root?.querySelector?.('[data-palette-sources-trigger]')?.focus?.();

            return true;
        },

        /** Up and Down move between the list's options, as Tab does. */
        rp_palSourcesKey(event) {
            const options = [...(this.$root?.querySelectorAll?.('[data-palette-source-option]') ?? [])];
            const at = options.indexOf(event?.target);

            if (at === -1 || (event.key !== 'ArrowDown' && event.key !== 'ArrowUp')) return;

            event.preventDefault?.();
            options[(at + (event.key === 'ArrowDown' ? 1 : options.length - 1)) % options.length]?.focus?.();
        },

        /** An option pressed: "All sources" clears the choice; a source joins it or leaves it. */
        rp_palSourceToggle(el) {
            const key = el?.getAttribute?.('data-palette-source-option') ?? '';

            this.rp_palSources = key === ''
                ? []
                : (this.rp_palSources.includes(key) ? this.rp_palSources.filter((chosen) => chosen !== key) : [...this.rp_palSources, key]);
            this.rp_palChanged();
        },

        /** A chosen source's chip ✕: that source leaves the choice. */
        rp_palSourceRemove(key) {
            this.rp_palSources = this.rp_palSources.filter((chosen) => chosen !== key);
            this.rp_palChanged();
            this.$root?.querySelector?.('[data-palette-sources-trigger]')?.focus?.();
        },

        /** An option's aria-pressed: "All sources" while none is chosen, a source while it is. */
        rp_palSourceChosen(el) {
            const key = el?.getAttribute?.('data-palette-source-option') ?? '';

            return (key === '' ? this.rp_palSources.length === 0 : this.rp_palSources.includes(key)) ? 'true' : 'false';
        },

        /** How many of a source's metrics and charts the search leaves, the option's count. */
        rp_palSourceCount(el) {
            const key = el?.getAttribute?.('data-palette-source-option') ?? '';
            let count = 0;

            for (const group of this.$root?.querySelectorAll?.(PALETTE_GROUPS) ?? []) {
                if (group.getAttribute?.('data-palette-source') !== key) continue;

                count += [...(group.querySelectorAll?.('[data-palette-search]') ?? [])].filter((entry) => this.rp_palTextShows(entry)).length;
            }

            for (const entry of this.$root?.querySelectorAll?.(PALETTE_DATASETS) ?? []) {
                if (entry.getAttribute?.('data-palette-source') === key && this.rp_palTextShows(entry)) count += 1;
            }

            return count;
        },

        /** A source's name for its chip, read off its option. */
        rp_palSourceLabel(key) {
            return [...(this.$root?.querySelectorAll?.('[data-palette-source-option]') ?? [])]
                .find((option) => option.getAttribute('data-palette-source-option') === key)
                ?.getAttribute?.('data-palette-source-name') ?? key;
        },

        /** The chips the Sources line shows: the first two chosen; the rest are counted as "+N". */
        rp_palSourceChips() {
            return this.rp_palSources.slice(0, 2);
        },

        /** Run `then` on the element `selector` finds once x-show has revealed it (Alpine shows it a frame after the change). */
        rp_palWhenShown(selector, then, tries = 10) {
            const element = this.$root?.querySelector?.(selector);

            if (element && element.offsetParent !== null) {
                then(element);

                return;
            }

            if (tries > 0) requestAnimationFrame(() => this.rp_palWhenShown(selector, then, tries - 1));
        },

        /** A section heading's button: its section shown or hidden. An open "Add as" menu would be left pointing at a moved row. */
        rp_palToggleSection(name) {
            this.rp_palAsKey = '';
            this.rp_palSections[name] = ! this.rp_palSections[name];
        },

        /** How many metrics the palette shows, of how many it lists. */
        rp_palCounts() {
            let shown = 0;
            let total = 0;

            for (const group of this.$root?.querySelectorAll?.(PALETTE_GROUPS) ?? []) {
                const entries = [...(group.querySelectorAll?.('[data-palette-search]') ?? [])];
                total += entries.length;

                if (this.rp_palSourceShows(group)) shown += entries.filter((entry) => this.rp_palTextShows(entry)).length;
            }

            return { shown, total };
        },

        rp_palCountText() {
            const { shown, total } = this.rp_palCounts();

            return paletteCountSentence(text, shown, total);
        },

        rp_palIsEmpty() {
            const { shown, total } = this.rp_palCounts();

            return total > 0 && shown === 0;
        },

        /**
         * The search or the filter changed: the list narrows at once, and the count is said once
         * the editor pauses, so a screen reader is not read a count per keystroke. An open "Add as"
         * menu closes, since its metric may just have been filtered out.
         */
        rp_palChanged() {
            this.rp_palAsKey = '';

            // Matches only under "Charts from connected data", which starts closed, would leave the
            // editor looking at "No metrics match" with the results out of sight: that section
            // opens. It is never closed from here, so a section the editor opened stays open.
            if (! this.rp_palSections.datasets && this.rp_palIsEmpty() && this.rp_palDatasetCounts().shown > 0) {
                this.rp_palSections.datasets = true;
            }

            clearTimeout(palAnnounceTimer);
            palAnnounceTimer = setTimeout(() => this.rp_arrangeAnnounce(this.rp_palCountText()), PALETTE_ANNOUNCE_DELAY);
        },

        /** The empty result's way out: everything shown again, and focus in the search. */
        rp_palClear() {
            this.rp_palQuery = '';
            this.rp_palSources = [];
            this.rp_palChanged();
            this.$root?.querySelector?.('[data-report-palette-search]')?.focus?.();
        },

        /**
         * Escape in the search clears it when it holds text, and stops there; in an empty search
         * it goes on to the palette, which closes. Otherwise one Escape would both clear the
         * search (the browser's own) and close the palette.
         */
        rp_palEscape(event) {
            if (this.rp_palQuery === '') return;

            event?.preventDefault?.();
            event?.stopPropagation?.();
            this.rp_palQuery = '';
            this.rp_palChanged();
        },

        /**
         * Escape anywhere in the panel that nothing inside handled (the search holding text does):
         * an open "Add as" menu closes first, with focus back on its chevron, then the panel, whose
         * server answer returns focus to what opened it.
         */
        rp_palEscapeKey() {
            if (this.rp_palSourcesClose(true)) return;

            if (this.rp_palAsKey !== '') {
                this.rp_palAsClose(true);

                return;
            }

            this.$wire.togglePalette();
        },

        /** An entry's Add: added at the end of the tab, its key read off the entry. */
        rp_palAdd(el) {
            this.$wire.addCard(el?.closest?.('[data-palette-key]')?.getAttribute?.('data-palette-key') ?? '');
        },

        /**
         * A dataset chart's Add: its data source dialog opens first (Settings\DataSource, opened by
         * PALETTE_DATASET_PICKER with the chart's key), which adds the chart with the source chosen,
         * or without one; Cancel adds nothing, and the dialog hands focus back to this button.
         */
        rp_palAddDataset(el) {
            const key = el?.closest?.('[data-palette-key]')?.getAttribute?.('data-palette-key') ?? '';

            if (key !== '') this.$dispatch(PALETTE_DATASET_PICKER, { key });
        },

        /**
         * A metric's "Add as" chevron: opens the menu for it, or closes it when it is that metric's.
         * One menu serves the whole list, since a copy per metric would be sent hundreds of times on
         * each render; it is placed in the panel's scrolling body under the chevron (a runtime value,
         * hence its style binding), or above it near the bottom (rp_palAsShown()), labelled with the
         * metric's name, and focus moves to its first choice that is not disabled once it shows.
         */
        rp_palAsToggle(el) {
            const entry = el?.closest?.('[data-palette-key]');
            const key = entry?.getAttribute?.('data-palette-key') ?? '';

            if (key === '') return;

            if (key === this.rp_palAsKey) {
                this.rp_palAsClose(true);

                return;
            }

            this.rp_palAsName = entry.querySelector?.('[data-palette-name]')?.textContent?.trim?.() ?? '';
            this.rp_palAsTypes = paletteChartTypes(entry.getAttribute?.('data-palette-types'));
            this.rp_palAsDefault = entry.getAttribute?.('data-palette-default') ?? '';
            this.rp_palSourcesOpen = false;
            this.rp_palAsKey = key;
            this.rp_palAsPlace(0);

            requestAnimationFrame(() => this.rp_palAsShown());
        },

        /**
         * Once the menu shows: placed where it fits, and focus on its first choice. x-show reveals it
         * a frame after the change (Alpine defers a show to the next animation frame so a click that
         * opened it is not taken for a click outside), so a focus asked for sooner lands on a hidden
         * button and is lost; this waits up to ten frames for it instead.
         */
        rp_palAsShown(tries = 10) {
            if (this.rp_palAsKey === '') return;

            const first = this.rp_palAsChoices()[0];

            if (! first) {
                if (tries > 0) requestAnimationFrame(() => this.rp_palAsShown(tries - 1));

                return;
            }

            this.rp_palAsPlace(this.$root?.querySelector?.('[data-palette-as-menu]')?.offsetHeight ?? 0);
            first.focus?.();
        },

        /** The menu's place under (or above) its chevron, for a menu `height` px tall. */
        rp_palAsPlace(height) {
            const trigger = this.rp_palAsTrigger();
            const body = trigger?.closest?.('[data-palette-body]');
            const at = trigger?.getBoundingClientRect?.();
            const box = body?.getBoundingClientRect?.();

            if (! at || ! box) return;

            const viewport = typeof window.innerHeight === 'number' ? window.innerHeight : Infinity;

            this.rp_palAsTop = paletteMenuTop(at, box, body.scrollTop ?? 0, Number(height) || 0, viewport);
        },

        /**
         * Tab on the chevron whose menu is open goes into the menu, as it would into a disclosure's
         * panel drawn after its button: the one menu sits at the end of the panel's body, so the
         * browser's own next stop would be the next metric's Add.
         */
        rp_palTabIntoMenu(event) {
            if (event?.shiftKey || event?.target == null || event.target !== this.rp_palAsTrigger()) return;

            const first = this.rp_palAsChoices()[0];

            if (! first) return;

            event.preventDefault?.();
            first.focus?.();
        },

        /**
         * A card just added from the panel (or the grid a row just went into): outlined and labelled
         * "Just added" for a few seconds, and, while the panel is the fixed side panel, scrolled into
         * view without moving focus out of the panel (at once under prefers-reduced-motion). Below
         * lg the panel sits above the grid, and scrolling would carry the focused Add off screen.
         */
        rp_palShowAdded(cardId) {
            if (typeof cardId !== 'string' || cardId === '') return;

            const card = [...(this.$root?.querySelectorAll?.('[data-site-analytics-card]') ?? [])]
                .find((element) => element.getAttribute('data-site-analytics-card') === cardId);

            showJustAdded(card, this.$root?.querySelector?.('[data-report-palette]'), text.paletteJustAdded);
        },

        /** A chevron's aria-expanded: whether the menu is open for its metric. */
        rp_palAsOpenFor(el) {
            const key = el?.closest?.('[data-palette-key]')?.getAttribute?.('data-palette-key') ?? '';

            return key !== '' && key === this.rp_palAsKey ? 'true' : 'false';
        },

        /** The chevron of the metric the menu is open for, found by attribute value. */
        rp_palAsTrigger() {
            const key = this.rp_palAsKey;

            if (key === '') return null;

            return [...(this.$root?.querySelectorAll?.('[data-report-palette] [data-palette-key]') ?? [])]
                .find((entry) => entry.getAttribute('data-palette-key') === key)
                ?.querySelector?.('[data-palette-as-trigger]') ?? null;
        },

        /** The menu closed; with `returnFocus`, focus goes back to its chevron. */
        rp_palAsClose(returnFocus = false) {
            const trigger = returnFocus ? this.rp_palAsTrigger() : null;

            this.rp_palAsKey = '';
            trigger?.focus?.();
        },

        /** The menu's choices that can take focus now: the open entry's (a metric's, or a chart's own types), enabled. */
        rp_palAsChoices() {
            const menu = this.$root?.querySelector?.('[data-palette-as-menu]');

            return [...(menu?.querySelectorAll?.('button') ?? [])].filter((button) => ! button.disabled && button.offsetParent !== null);
        },

        /** The menu's position in the panel's body, a runtime value. */
        rp_palAsStyle() {
            return { top: `${this.rp_palAsTop}px` };
        },

        /** Whether a "Row in <grid>" choice is refused for the open metric: its grid already holds it. */
        rp_palAsHeld(el) {
            return paletteGridHolds(el?.getAttribute?.('data-palette-held'), this.rp_palAsKey);
        },

        /**
         * A choice in the menu: the metric added as it (Show::addCardAs(), which checks the choice
         * again). Focus goes back to the chevron at once, rather than to the page as the menu hides,
         * and the server's answer then moves it to the metric's Add.
         */
        rp_palAddAs(el) {
            const key = this.rp_palAsKey;
            const as = el?.getAttribute?.('data-palette-as') ?? '';

            if (key === '' || as === '' || el?.disabled) return;

            this.rp_palAsClose(true);

            // A chart from connected data asks for its data source first, then is added as `as`.
            if (key.startsWith('dataset:')) {
                this.$dispatch(PALETTE_DATASET_PICKER, { key, type: as });

                return;
            }

            this.$wire.addCardAs(key, as);
        },

        /** A click outside the menu closes it; one on a chevron is that chevron's to toggle. */
        rp_palAsOutside(event) {
            if (event?.target?.closest?.('[data-palette-as-trigger]')) return;

            this.rp_palAsKey = '';
        },

        /**
         * Tab past the menu's last choice, or Shift+Tab before its first, closes it and goes on from
         * its chevron, as a menu button's Tab does: the one menu sits at the end of the panel's body,
         * so the browser's own next stop would skip every metric after this one. Shift+Tab lands on
         * the chevron; Tab on the next control after it that can take focus.
         */
        rp_palAsTab(event) {
            const menu = this.$root?.querySelector?.('[data-palette-as-menu]');
            const choices = this.rp_palAsChoices();
            const edge = event?.shiftKey ? choices[0] : choices[choices.length - 1];

            if (edge === undefined || event?.target !== edge) return;

            event.preventDefault?.();

            const trigger = this.rp_palAsTrigger();
            const panel = trigger?.closest?.('[data-report-palette]');

            this.rp_palAsKey = '';

            if (event.shiftKey) {
                trigger?.focus?.();

                return;
            }

            const stops = [...(panel?.querySelectorAll?.('button, input, select, a[href]') ?? [])]
                .filter((element) => ! element.disabled && element.offsetParent !== null && ! menu.contains(element));
            const at = stops.indexOf(trigger);

            ((at === -1 ? null : stops[at + 1]) ?? trigger)?.focus?.();
        },

        /** The arrange region's rp_announce(): cleared first, so the same sentence twice is said twice. */
        rp_arrangeAnnounce(message) {
            this.rp_arrangeStatus = '';
            requestAnimationFrame(() => {
                this.rp_arrangeStatus = message;
            });
        },

        /**
         * A host drew a contract: the key already under it, printed from the load, takes the colours
         * the chart was drawn in, and the card is marked drawn, since the host's sr-only table now
         * reads it. Only the swatches change, so the card keeps its size.
         */
        rp_drawn(event) {
            const cardId = cardIdOf(event?.detail?.id);

            if (cardId === null) return;

            const slot = this.rp_slot(cardId);
            const card = this.rp_cards[slot];

            if (! card) return;

            const key = card.key?.length ? keyInDrawnColours(card.key, event.detail) : card.key;
            this.rp_cards = { ...this.rp_cards, [slot]: { ...card, key, drawn: true } };
        },

        /** A host's draw failed after it was sent a contract: its table is gone with its chart. */
        rp_hostFailed(event) {
            this.rp_setDrawn(cardIdOf(event?.detail?.id), false);
        },

        rp_setDrawn(cardId, drawn) {
            if (cardId === null) return;

            const slot = this.rp_slot(cardId);
            const card = this.rp_cards[slot];

            if (! card || card.drawn === drawn) return;

            this.rp_cards = { ...this.rp_cards, [slot]: { ...card, drawn } };
        },

        /**
         * Cleared first and set on the next frame, so a second load saying the same sentence (a
         * card that fails again after Retry) changes the region's text and is spoken again.
         */
        rp_announce(message) {
            this.rp_status = '';
            requestAnimationFrame(() => {
                this.rp_status = message;
            });
        },

        /** The ⓘ's sentences and the footer's key for the card an element sits in; the id is read, never written into an expression. */
        rp_linesFor(el) {
            return this.rp_cards[this.rp_slot(this.rp_cardOf(el))]?.lines ?? [];
        },

        /** Whether the card an element sits in has anything for its ⓘ to list, and so draws one. */
        rp_infoShownFor(el) {
            return hasInfo(this.rp_cards[this.rp_slot(this.rp_cardOf(el))]);
        },

        rp_infoOpenFor(el) {
            return this.rp_infoCard !== '' && this.rp_infoCard === this.rp_slot(this.rp_cardOf(el)) && this.rp_infoShownFor(el);
        },

        /** How many filters the reader set could not narrow the card: its ⓘ's dot. */
        rp_infoMissedFor(el) {
            return this.rp_cards[this.rp_slot(this.rp_cardOf(el))]?.missed ?? 0;
        },

        /**
         * Whether the ⓘ carries its dot: a filter the reader set could not narrow the card, or one of
         * its notes says its figures stopped moving (`warns`, Show::footer()).
         */
        rp_infoDotFor(el) {
            return this.rp_infoMissedFor(el) > 0 || this.rp_cards[this.rp_slot(this.rp_cardOf(el))]?.warns === true;
        },

        /** The dot in words, which describes the ⓘ's button; '' without one. */
        rp_infoMissedText(el) {
            const count = this.rp_infoMissedFor(el);
            const warns = this.rp_cards[this.rp_slot(this.rp_cardOf(el))]?.warns === true;

            if (count < 1) return warns ? (text.infoWarning ?? 'This card has a warning.') : '';

            const sentence = warns
                ? (count === 1 ? (text.infoMissedOneWarning ?? ':count filter doesn’t apply; this card has a warning.') : (text.infoMissedManyWarning ?? ':count filters don’t apply; this card has a warning.'))
                : (count === 1 ? (text.infoMissedOne ?? ':count filter doesn’t apply to this card.') : (text.infoMissedMany ?? ':count filters don’t apply to this card.'));

            return sentence.replace(':count', String(count));
        },

        /** The panel's "Filters applied:" line, its values in the words the bar's options give them. */
        rp_infoAppliedFor(el) {
            return appliedFiltersText(this.rp_cards[this.rp_slot(this.rp_cardOf(el))]?.applied ?? [], this.$wire?.filterOptions, text.infoMore ?? '+:count more');
        },

        /**
         * The ⓘ's click, Enter or Space: its panel opens, closing any other on the page, or closes.
         * Focus stays on the button. Opening says so to the card's edit toolbar, whose "⋯" panel
         * would lie under this one.
         */
        rp_infoToggle(el) {
            const slot = this.rp_slot(this.rp_cardOf(el));

            if (this.rp_infoCard === slot) {
                this.rp_infoClose(false);

                return;
            }

            this.rp_infoCard = slot;
            infoRoot = el.closest('[data-card-info]');
            el.dispatchEvent(new CustomEvent('report-card-info-open', { bubbles: true }));
        },

        /** The open panel closed; with `returnFocus`, focus back on its button while it is on the page. */
        rp_infoClose(returnFocus) {
            if (this.rp_infoCard === '') return;

            const button = infoRoot?.isConnected ? infoRoot.querySelector('[data-report-card-info]') : null;

            this.rp_infoCard = '';
            infoRoot = null;

            if (returnFocus) button?.focus();
        },

        /**
         * Focus leaving the ⓘ for somewhere else on the page (Tab past it, or another control
         * pressed) closes its panel, as the edit toolbar's disclosures close. Focus going nowhere
         * (a click on the panel's text) keeps it open.
         */
        rp_infoFocusOut(root, event) {
            if (root === infoRoot && event?.relatedTarget && ! root.contains(event.relatedTarget)) this.rp_infoClose(false);
        },

        rp_keyFor(el) {
            return this.rp_cards[this.rp_slot(this.rp_cardOf(el))]?.key ?? [];
        },

        /**
         * Whether a card's key is hidden from assistive tech: a donut's, and only once its chart has
         * drawn. The key prints every slice's figure from the load, before and whether or not the
         * chart draws, but the host's sr-only table that reads the same figures exists only once it
         * has. Hidden any earlier (loading, below the fold, failed), sighted readers would see every
         * figure and a screen reader would get none.
         */
        rp_keyHiddenFor(el) {
            const card = this.rp_cards[this.rp_slot(this.rp_cardOf(el))];

            return card?.drawn === true && (card.key ?? []).some((entry) => entry.swatch === 'slice');
        },

        rp_cardOf(el) {
            return el?.closest?.('[data-site-analytics-card]')?.getAttribute('data-site-analytics-card') ?? '';
        },

        /** A stored id may be any string, "__proto__" included, so it is never an object key on its own. */
        rp_slot(cardId) {
            return `card:${cardId}`;
        },

        rp_strings(list) {
            return Array.isArray(list) ? list.filter((entry) => typeof entry === 'string' && entry !== '') : [];
        },
    };
}
